import React, { useState, useRef, useCallback, useEffect, lazy, Suspense } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { api } from '../lib/api'
import { fetchAuthenticatedFile } from '../lib/files'
import { useAuthStore } from '../store/auth'

const PdfPageSelector = lazy(() => import('../components/floorplan/PdfPageSelector'))
import DeviceIcon, { detectIconType, detectDeviceType, colorForType, generateLabel, DEFAULT_LABEL_CONFIG } from '../components/floorplan/DeviceIcon'
import type { LabelConfig, LabelTypeConfig } from '../components/floorplan/DeviceIcon'
import '../styles/floorplan.css'
import type { FloorPlan, PlacedDevice as PlacedDeviceType, CableRun as CableRunType, FloorPlanRoom as FloorPlanRoomType } from '@shared/types'

// ═══════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════

const fmt = (n: number | undefined | null) => (n ?? 0).toFixed(1)

// Placeable catalog item (mapped from API response)
interface PlaceableCatalogItem {
  id: string
  name: string
  brand: string
  mpn: string
  type: string
  icon: string
  fov: number
  range: number
  color: string
}

const CATEGORIES = [
  { id: 'all', label: 'All', icon: '\u229E' },
  { id: 'camera', label: 'Cameras', icon: '\u25C9' },
  { id: 'switch', label: 'Switches', icon: '\u229F' },
  { id: 'ap', label: 'APs', icon: '\u25CE' },
  { id: 'reader', label: 'Readers', icon: '\u25C8' },
  { id: 'controller', label: 'Controllers', icon: '\u25A3' },
  { id: 'power', label: 'Power', icon: '\u26A1' },
  { id: 'nvr', label: 'NVR/Server', icon: '\u25A4' },
]

// Item types that represent physical, placeable devices (not labor/software)
const PLACEABLE_CATEGORIES = new Set([
  'surveillance', 'camera', 'cameras',
  'networking', 'network',
  'access_control', 'access control',
  'power',
  'recording', 'computers',
])

// Default FOV values by icon type
const DEFAULT_FOV: Record<string, number> = {
  dome: 108, bullet: 90, pano: 360, ptz: 180,
}
const DEFAULT_RANGE: Record<string, number> = {
  dome: 80, bullet: 150, pano: 60, ptz: 200,
}

// Map API catalog response to placeable items
function mapCatalogToPlaceable(items: Record<string, unknown>[]): PlaceableCatalogItem[] {
  return items
    .filter(item => {
      const cat = ((item.category || item.custom_category || '') as string).toLowerCase()
      const itemType = ((item.item_type || '') as string).toLowerCase()
      // Filter: only physical devices, not labor or software
      if (itemType === 'labor' || itemType === 'software' || itemType === 'service') return false
      return PLACEABLE_CATEGORIES.has(cat) || !cat // include uncategorized items
    })
    .map(item => {
      const product = {
        name: (item.name || item.custom_name || '') as string,
        category: (item.category || item.custom_category || '') as string,
        custom_name: item.custom_name as string | null,
        custom_category: item.custom_category as string | null,
      }
      const icon = detectIconType(product)
      const type = detectDeviceType(product)
      return {
        id: item.id as string,
        name: (item.name || item.custom_name || 'Unknown') as string,
        brand: (item.brand || item.custom_brand || '') as string,
        mpn: (item.mpn || item.custom_mpn || '') as string,
        type,
        icon,
        fov: DEFAULT_FOV[icon] || 0,
        range: DEFAULT_RANGE[icon] || 0,
        color: colorForType(type),
      }
    })
}

const LAYERS_INIT = [
  { id: 'cameras', label: 'Cameras', color: 'var(--color-camera)', visible: true },
  { id: 'networking', label: 'Networking', color: 'var(--color-networking)', visible: true },
  { id: 'access', label: 'Access Control', color: 'var(--color-access)', visible: true },
  { id: 'power', label: 'Power', color: 'var(--color-power)', visible: true },
  { id: 'infrastructure', label: 'Infrastructure', color: 'var(--color-infra)', visible: true },
  { id: 'cables', label: 'Cable Runs', color: 'var(--color-cable)', visible: true },
  { id: 'measurements', label: 'Measurements', color: '#ec4899', visible: true },
]

const LAYER_MAP: Record<string, string> = {
  camera: 'cameras', switch: 'networking', ap: 'networking',
  reader: 'access', controller: 'access',
  power: 'power', nvr: 'infrastructure', server: 'infrastructure',
  mdf: 'infrastructure', idf: 'infrastructure',
}

const CABLE_COLORS: Record<string, string> = {
  cat5e: 'var(--color-cable)',
  cat6: 'var(--color-cable)',
  cat6a: 'var(--color-cable)',
  fiber: 'var(--accent)',
  coax: 'var(--color-infra)',
  other: 'var(--color-infra)',
}

const TOOLS = {
  SELECT: 'select', PLACE: 'place', CABLE: 'cable',
  MEASURE: 'measure', ROOM: 'room', MDF: 'mdf', CALIBRATE: 'calibrate',
} as const

type ToolType = typeof TOOLS[keyof typeof TOOLS]

// ── Local device/cable types (canvas state) ──

interface CanvasDevice {
  id: string
  slug?: string
  catalogId: string
  x: number
  y: number
  angle: number
  label: string
  name: string
  type: string
  icon: string
  fov: number
  range: number
  color: string
  // Feature 3 & 4: Extended device details
  is_outdoor: boolean
  ip_address: string
  mac_address: string
  vlan: string
  idf_id: string
  switch_device_id: string
  switch_port: string
  patch_panel_id: string
  patch_port: string
  mount_type: string
  cable_type: string
  cable_length_ft: number | null
  install_status: string
  installed_by: string
}

const MOUNT_TYPES = ['Wall Mount', 'Ceiling Mount', 'Pole Mount', 'Corner Mount', 'Pendant', 'Backbox', 'EMT Custom', 'WMS', 'WML', 'Surface Mount']
const CABLE_TYPES = ['Plenum Cat5e', 'Plenum Cat6', 'Plenum Cat6a', 'Riser Cat5e', 'Riser Cat6', 'UV Cat5e', 'UV Cat6', 'Direct Burial Cat6', 'Fiber SM', 'Fiber MM', 'Coax RG59', 'Coax RG6', '22/6', '22/4', '18/2', '18/4', '16/2', '14/2', '12/2', 'HDMI', 'HDBaseT']

const CABLE_TYPE_DEFAULTS: Record<string, string> = {
  reader: '22/6',
  rex: '22/4',
  door_contact: '22/4',
  electric_strike: '18/2',
  mag_lock: '18/2',
  camera: 'Plenum Cat6',
  ap: 'Plenum Cat6',
  switch: 'Plenum Cat6',
  intercom: 'Plenum Cat6',
  door_station: 'Plenum Cat6',
  speaker: '16/2',
  analog_camera: 'Coax RG59',
  smoke_detector: '18/4',
  pull_station: '18/4',
  horn_strobe: '14/2',
  data_port: 'Plenum Cat6',
  keystone: 'Plenum Cat6',
  projector: 'Plenum Cat6',
  display: 'HDMI',
  controller: 'Plenum Cat6',
  nvr: 'Plenum Cat6',
  server: 'Plenum Cat6',
  power: '12/2',
  ups: '12/2',
}

const INSTALL_STATUSES = ['Planned', 'Installed', 'Active', 'Offline', 'Decommissioned']

const GENERIC_DEVICES: PlaceableCatalogItem[] = [
  { id: 'gen-ip-camera', name: 'IP Camera', brand: '', mpn: '', icon: 'dome', type: 'camera', fov: 108, range: 80, color: colorForType('camera') },
  { id: 'gen-analog-camera', name: 'Analog Camera', brand: '', mpn: '', icon: 'bullet', type: 'camera', fov: 90, range: 150, color: colorForType('camera') },
  { id: 'gen-card-reader', name: 'Card Reader', brand: '', mpn: '', icon: 'reader', type: 'reader', fov: 0, range: 0, color: colorForType('reader') },
  { id: 'gen-ac-panel', name: 'Access Control Panel', brand: '', mpn: '', icon: 'controller', type: 'controller', fov: 0, range: 0, color: colorForType('controller') },
  { id: 'gen-switch-8', name: 'Switch (8-port)', brand: '', mpn: '', icon: 'switch', type: 'switch', fov: 0, range: 0, color: colorForType('switch') },
  { id: 'gen-switch-24', name: 'Switch (24-port)', brand: '', mpn: '', icon: 'switch', type: 'switch', fov: 0, range: 0, color: colorForType('switch') },
  { id: 'gen-switch-48', name: 'Switch (48-port)', brand: '', mpn: '', icon: 'switch', type: 'switch', fov: 0, range: 0, color: colorForType('switch') },
  { id: 'gen-ap', name: 'Wireless AP', brand: '', mpn: '', icon: 'ap', type: 'ap', fov: 0, range: 0, color: colorForType('ap') },
  { id: 'gen-data-port', name: 'Data Port', brand: '', mpn: '', icon: 'switch', type: 'switch', fov: 0, range: 0, color: colorForType('switch') },
  { id: 'gen-nvr', name: 'NVR / Video Recorder', brand: '', mpn: '', icon: 'server', type: 'nvr', fov: 0, range: 0, color: colorForType('nvr') },
  { id: 'gen-door-station', name: 'Door Station / Intercom', brand: '', mpn: '', icon: 'reader', type: 'reader', fov: 0, range: 0, color: colorForType('reader') },
  { id: 'gen-speaker', name: 'Speaker', brand: '', mpn: '', icon: 'controller', type: 'controller', fov: 0, range: 0, color: colorForType('controller') },
  { id: 'gen-display', name: 'TV / Display', brand: '', mpn: '', icon: 'controller', type: 'controller', fov: 0, range: 0, color: colorForType('controller') },
  { id: 'gen-rack', name: 'Equipment Rack', brand: '', mpn: '', icon: 'mdf', type: 'server', fov: 0, range: 0, color: colorForType('server') },
  { id: 'gen-ups', name: 'UPS / Power Supply', brand: '', mpn: '', icon: 'ups', type: 'power', fov: 0, range: 0, color: colorForType('power') },
  { id: 'gen-server', name: 'Server', brand: '', mpn: '', icon: 'server', type: 'server', fov: 0, range: 0, color: colorForType('server') },
  { id: 'gen-workstation', name: 'Workstation', brand: '', mpn: '', icon: 'server', type: 'server', fov: 0, range: 0, color: colorForType('server') },
  { id: 'gen-smoke', name: 'Smoke Detector', brand: '', mpn: '', icon: 'dome', type: 'camera', fov: 0, range: 0, color: colorForType('camera') },
  { id: 'gen-pull-station', name: 'Pull Station', brand: '', mpn: '', icon: 'reader', type: 'reader', fov: 0, range: 0, color: colorForType('reader') },
  { id: 'gen-horn-strobe', name: 'Horn/Strobe', brand: '', mpn: '', icon: 'controller', type: 'controller', fov: 0, range: 0, color: colorForType('controller') },
  { id: 'gen-door-contact', name: 'Door Contact', brand: '', mpn: '', icon: 'reader', type: 'reader', fov: 0, range: 0, color: colorForType('reader') },
  { id: 'gen-rex', name: 'REX (Request to Exit)', brand: '', mpn: '', icon: 'reader', type: 'reader', fov: 0, range: 0, color: colorForType('reader') },
  { id: 'gen-electric-strike', name: 'Electric Strike', brand: '', mpn: '', icon: 'controller', type: 'controller', fov: 0, range: 0, color: colorForType('controller') },
  { id: 'gen-mag-lock', name: 'Magnetic Lock', brand: '', mpn: '', icon: 'controller', type: 'controller', fov: 0, range: 0, color: colorForType('controller') },
  { id: 'gen-keypad', name: 'Keypad', brand: '', mpn: '', icon: 'reader', type: 'reader', fov: 0, range: 0, color: colorForType('reader') },
  { id: 'gen-motion', name: 'Motion Sensor', brand: '', mpn: '', icon: 'dome', type: 'camera', fov: 0, range: 0, color: colorForType('camera') },
]

interface CanvasCable {
  id: string
  slug?: string
  from: string
  to: string
  type: string
  color: string
}

interface CanvasInfra {
  id: string
  slug?: string
  x: number
  y: number
  type: string
  name: string
}

interface FloorTab {
  id: string
  slug: string
  name: string
}

// ── FOV CONE ──

function FovCone({ x, y, angle = 0, fov = 90, range = 80, color = 'var(--color-camera)' }: {
  x: number; y: number; angle?: number; fov?: number; range?: number; color?: string
}) {
  const startAngle = (angle - fov / 2) * Math.PI / 180
  const endAngle = (angle + fov / 2) * Math.PI / 180
  const x1 = x + range * Math.cos(startAngle)
  const y1 = y + range * Math.sin(startAngle)
  const x2 = x + range * Math.cos(endAngle)
  const y2 = y + range * Math.sin(endAngle)
  const largeArc = fov > 180 ? 1 : 0
  return (
    <path
      d={`M${x},${y} L${x1},${y1} A${range},${range} 0 ${largeArc},1 ${x2},${y2} Z`}
      fill={color} fillOpacity={0.08}
      stroke={color} strokeOpacity={0.25} strokeWidth={1} strokeDasharray="4 3"
    />
  )
}

// ── TOOL BUTTON ──

function ToolBtn({ icon, label, active, onClick }: {
  icon: string; label?: string; active?: boolean; onClick: () => void
}) {
  return (
    <button onClick={onClick} className={`fp-tool-btn ${active ? 'active' : ''}`} title={label}>
      <span className="fp-tool-icon">{icon}</span>
      {label && <span className="fp-tool-label">{label}</span>}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

function FloorPlanDesignerInner() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isSitePlan = location.pathname.startsWith('/site-plans/')
  const apiBase = isSitePlan ? '/plans' : '/floor-plans'
  // Designer renders inside the shell — sidebar + topbar stay visible

  // ── Plan data ──
  const [plan, setPlan] = useState<FloorPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [_pdfConversionFailed, setPdfConversionFailed] = useState(false)
  const [pdfFileForSelector, setPdfFileForSelector] = useState<File | null>(null)
  const [floors] = useState<FloorTab[]>([])
  const [activeFloor, setActiveFloor] = useState('')

  // ── Catalog from API ──
  const [catalog, setCatalog] = useState<PlaceableCatalogItem[]>([])
  const [catalogLoading, setCatalogLoading] = useState(true)

  // ── Canvas state ──
  const [tool, setTool] = useState<ToolType>(TOOLS.SELECT)
  const [devices, setDevices] = useState<CanvasDevice[]>([])
  const [cables, setCables] = useState<CanvasCable[]>([])
  const [infra, setInfra] = useState<CanvasInfra[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedCatalog, setSelectedCatalog] = useState<string | null>(null)
  const [catFilter, setCatFilter] = useState('all')
  const [catalogSearch, setCatalogSearch] = useState('')
  const [favorites, setFavorites] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('fp-favorites') || '[]') } catch { return [] }
  })
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('fp-recent') || '[]') } catch { return [] }
  })
  const [collapsedPanelSections, setCollapsedPanelSections] = useState<Set<string>>(new Set())
  const [layers, setLayers] = useState(LAYERS_INIT)
  const [showCatalog, setShowCatalog] = useState(true)
  const [showProperties, setShowProperties] = useState(false)
  const [showLegend, setShowLegend] = useState(false)
  const [showLayers, setShowLayers] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  const [dragDevice, setDragDevice] = useState<string | null>(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [cableStart, setCableStart] = useState<string | null>(null)
  const [selectedCableId, setSelectedCableId] = useState<string | null>(null)
  const [measurePoints, setMeasurePoints] = useState<{ x: number; y: number }[]>([])
  const [scale, setScale] = useState(4.2)
  const [calibrated, setCalibrated] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [dragover, setDragover] = useState(false)

  // Calibration state
  const [calibPoints, setCalibPoints] = useState<{ x: number; y: number }[]>([])
  const [showCalibModal, setShowCalibModal] = useState(false)
  const [calibInput, setCalibInput] = useState('')

  // Room drawing state
  const [roomVertices, setRoomVertices] = useState<{ x: number; y: number }[]>([])
  const [rooms, setRooms] = useState<{ id: string; slug?: string; name: string; vertices: { x: number; y: number }[]; color: string }[]>([])
  const [showRoomModal, setShowRoomModal] = useState(false)
  const [roomNameInput, setRoomNameInput] = useState('')

  // MDF/IDF modal state
  const [showMdfModal, setShowMdfModal] = useState(false)
  const [mdfPendingPos, setMdfPendingPos] = useState<{ x: number; y: number } | null>(null)
  const [mdfType, setMdfType] = useState<'mdf' | 'idf'>('idf')
  const [mdfNameInput, setMdfNameInput] = useState('IDF-01')

  // Auto-save status
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; deviceId?: string } | null>(null)

  // Label config
  const [labelConfig, setLabelConfig] = useState<LabelConfig>(DEFAULT_LABEL_CONFIG)
  const [showLabelConfig, setShowLabelConfig] = useState(false)

  // Snap-to-grid
  const [snapEnabled, setSnapEnabled] = useState(false)
  const [alignmentGuides, setAlignmentGuides] = useState<{ type: 'vertical' | 'horizontal'; x?: number; y?: number; x1: number; y1: number; x2: number; y2: number }[]>([])

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectionBox, setSelectionBox] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null)
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null)

  // Copy/paste clipboard
  const clipboardRef = useRef<CanvasDevice[]>([])

  // Cable settings
  const [cableSlackPct] = useState(15)

  // Export / Import
  const [showExportMenu, setShowExportMenu] = useState(false)
  const importFileRef = useRef<HTMLInputElement>(null)

  // 3D scan link
  const [scan3dUrl, setScan3dUrl] = useState('')
  const [showScan3dModal, setShowScan3dModal] = useState(false)

  // Legend draggable state
  const [legendPos, setLegendPos] = useState({ x: typeof window !== 'undefined' ? window.innerWidth - 320 : 900, y: typeof window !== 'undefined' ? window.innerHeight - 400 : 400 })
  const [legendDragging, setLegendDragging] = useState(false)
  const legendDragOffset = useRef({ x: 0, y: 0 })
  const [legendCollapsed, setLegendCollapsed] = useState(false)
  const [legendCompact, setLegendCompact] = useState(false)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())

  // Layers panel draggable state
  const [layersPos, setLayersPos] = useState({ x: typeof window !== 'undefined' ? window.innerWidth - 320 : 900, y: 80 })
  const [layersDragging, setLayersDragging] = useState(false)
  const layersDragOffset = useRef({ x: 0, y: 0 })

  // Undo/redo
  interface UndoAction {
    type: 'add' | 'delete' | 'move' | 'cable_add' | 'cable_delete'
    device?: CanvasDevice
    cable?: CanvasCable
    previousPosition?: { x: number; y: number }
    deviceId?: string
  }
  const [undoStack, setUndoStack] = useState<UndoAction[]>([])
  const [redoStack, setRedoStack] = useState<UndoAction[]>([])

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const svgRef = useRef<SVGSVGElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selected = devices.find(d => d.id === selectedId)

  // ── LOAD CATALOG FROM API ──

  useEffect(() => {
    setCatalogLoading(true)
    api.get<{ data: Record<string, unknown>[]; tenantProducts: Record<string, unknown>[] }>('/catalog?limit=100')
      .then(res => {
        const items = res.data || res.tenantProducts || []
        setCatalog(mapCatalogToPlaceable(items))
      })
      .catch(() => {
        // Catalog load failed — use empty list
        setCatalog([])
      })
      .finally(() => setCatalogLoading(false))
  }, [])

  // ── DATA LOADING (floor-plans API) ──

  interface FloorPlanWithDetails extends FloorPlan {
    rooms: FloorPlanRoomType[]
    devices: PlacedDeviceType[]
    cables: CableRunType[]
  }

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    api.get<FloorPlanWithDetails>(`${apiBase}/${slug}`)
      .then(rawData => {
        // Map site plan fields to floor plan fields
        const data: FloorPlanWithDetails = isSitePlan ? {
          ...rawData,
          canvas_width: (rawData as unknown as Record<string, unknown>).width_px as number || rawData.canvas_width || 1920,
          canvas_height: (rawData as unknown as Record<string, unknown>).height_px as number || rawData.canvas_height || 1080,
          scale_calibrated: rawData.scale_calibrated ?? 0,
          project_id: rawData.project_id || '',
          devices: rawData.devices || [],
          rooms: rawData.rooms || [],
          cables: rawData.cables || [],
        } : rawData
        setPlan(data)
        if (data.scale_px_per_ft) {
          setScale(data.scale_px_per_ft)
          setCalibrated(true)
        }

        // Map PlacedDevice[] to canvas devices
        const devs = data.devices || []
        const mapped: CanvasDevice[] = devs.map((d, i) => {
          const devType = d.device_type || 'camera'
          const icon = detectIconType({ category: devType })
          const color = colorForType(devType)
          const rec = d as unknown as Record<string, unknown>
          return {
            id: d.id,
            slug: d.slug,
            catalogId: d.catalog_item_id || '',
            x: d.x,
            y: d.y,
            angle: d.rotation || 0,
            label: d.label || `DEV-${String(i + 1).padStart(2, '0')}`,
            name: d.label || 'Device',
            type: devType,
            icon,
            fov: d.h_fov_degrees || DEFAULT_FOV[icon] || 0,
            range: d.max_range_ft || DEFAULT_RANGE[icon] || 0,
            color,
            is_outdoor: !!(rec.is_outdoor),
            ip_address: (rec.ip_address as string) || '',
            mac_address: (rec.mac_address as string) || '',
            vlan: (rec.vlan as string) || '',
            idf_id: (rec.idf_id as string) || '',
            switch_device_id: (rec.switch_device_id as string) || '',
            switch_port: (rec.switch_port as string) || '',
            patch_panel_id: (rec.patch_panel_id as string) || '',
            patch_port: (rec.patch_port as string) || '',
            mount_type: (rec.mount_type as string) || '',
            cable_type: (rec.cable_type as string) || '',
            cable_length_ft: (rec.cable_length_ft as number) || null,
            install_status: (rec.install_status as string) || 'Planned',
            installed_by: (rec.installed_by as string) || '',
          }
        })
        setDevices(mapped)

        // Map CableRun[] to canvas cables
        const cbl = data.cables || []
        setCables(cbl.filter(c => c.from_device_id && c.to_device_id).map(c => ({
          id: c.id,
          slug: c.slug,
          from: c.from_device_id!,
          to: c.to_device_id!,
          type: c.cable_type || 'cat6',
          color: 'var(--color-cable)',
        })))

        // Map rooms
        const rms = data.rooms || []
        setRooms(rms.map((r: FloorPlanRoomType) => {
          let vertices: { x: number; y: number }[] = []
          try { vertices = JSON.parse(r.polygon) } catch { vertices = [] }
          return { id: r.id, slug: r.slug, name: r.name, vertices, color: r.fill_color?.replace(/[0-9a-f]{2}$/i, '') || 'var(--accent)' }
        }))

        setActiveFloor(data.id)
      })
      .catch(err => setError(err?.message || 'Failed to load plan'))
      .finally(() => setLoading(false))
  }, [slug, apiBase, isSitePlan])

  // ── HELPERS ──

  // All placeable items: generic + catalog
  const allItems = [...GENERIC_DEVICES, ...catalog]

  const toggleFavorite = useCallback((itemId: string) => {
    setFavorites(prev => {
      const next = prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
      localStorage.setItem('fp-favorites', JSON.stringify(next))
      return next
    })
  }, [])

  const trackRecentlyUsed = useCallback((itemId: string) => {
    setRecentlyUsed(prev => {
      const next = [itemId, ...prev.filter(id => id !== itemId)].slice(0, 10)
      localStorage.setItem('fp-recent', JSON.stringify(next))
      return next
    })
  }, [])

  const togglePanelSection = useCallback((section: string) => {
    setCollapsedPanelSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }, [])

  const screenToCanvas = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current
    if (!svg) return { x: 0, y: 0 }
    const rect = svg.getBoundingClientRect()
    return {
      x: (clientX - rect.left - pan.x) / zoom,
      y: (clientY - rect.top - pan.y) / zoom,
    }
  }, [zoom, pan])

  const isLayerVisible = (deviceType: string) => {
    const layerId = LAYER_MAP[deviceType] || 'infrastructure'
    return layers.find(l => l.id === layerId)?.visible !== false
  }

  const toggleLayer = (layerId: string) => {
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, visible: !l.visible } : l))
  }

  // ── Z-KEY STATE (for zoom modifier) ──
  const isZHeldRef = useRef(false)
  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === 'z' && !e.ctrlKey && !e.metaKey && !(e.target as HTMLElement).matches('input,textarea')) isZHeldRef.current = true }
    const up = (e: KeyboardEvent) => { if (e.key === 'z') isZHeldRef.current = false }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up) }
  }, [])

  // ── CANVAS EVENTS ──

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault()

    // Horizontal scroll → pan left/right
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && !isZHeldRef.current) {
      setPan(p => ({ x: p.x - e.deltaX, y: p.y }))
      return
    }

    // Z held + vertical scroll → zoom toward cursor
    if (isZHeldRef.current || e.ctrlKey) {
      const svg = svgRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const cursorX = e.clientX - rect.left
      const cursorY = e.clientY - rect.top
      const factor = e.deltaY > 0 ? 0.9 : 1.1

      setZoom(prevZoom => {
        const newZoom = Math.max(0.2, Math.min(5, prevZoom * factor))
        const scale = newZoom / prevZoom
        // Adjust pan to keep cursor position stable
        setPan(p => ({
          x: cursorX - scale * (cursorX - p.x),
          y: cursorY - scale * (cursorY - p.y),
        }))
        return newZoom
      })
      return
    }

    // Default: vertical scroll → pan up/down
    setPan(p => ({ x: p.x, y: p.y - e.deltaY }))
  }, [])

  // Attach wheel listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    svg.addEventListener('wheel', handleWheel, { passive: false })
    return () => svg.removeEventListener('wheel', handleWheel)
  }, [handleWheel])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const onDevice = !!(e.target as HTMLElement).closest('[data-device]')
    // Rubber band selection: shift+drag on empty canvas in select mode
    if (e.button === 0 && tool === TOOLS.SELECT && !onDevice && e.shiftKey) {
      const pt = screenToCanvas(e.clientX, e.clientY)
      selectionStartRef.current = pt
      setSelectionBox({ x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y })
      e.preventDefault()
      return
    }
    if (e.button === 1 || (e.button === 0 && e.altKey) ||
        (e.button === 0 && tool === TOOLS.SELECT && !onDevice)) {
      setIsPanning(true)
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
      e.preventDefault()
    }
  }, [pan, tool, screenToCanvas])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y })
    }
    // Rubber band selection
    if (selectionBox && selectionStartRef.current) {
      const pt = screenToCanvas(e.clientX, e.clientY)
      setSelectionBox({ x1: selectionStartRef.current.x, y1: selectionStartRef.current.y, x2: pt.x, y2: pt.y })
    }
    if (dragDevice) {
      const { x: rawX, y: rawY } = screenToCanvas(e.clientX, e.clientY)
      let nx = rawX - dragOffset.x
      let ny = rawY - dragOffset.y

      // Snap-to-grid
      if (snapEnabled) {
        const gridSize = calibrated ? scale * 5 : 40 // 5ft or 40px
        nx = Math.round(nx / gridSize) * gridSize
        ny = Math.round(ny / gridSize) * gridSize
      }

      // Alignment guides
      const threshold = 5
      const guides: typeof alignmentGuides = []
      devices.forEach(d => {
        if (d.id === dragDevice) return
        if (Math.abs(d.x - nx) < threshold) {
          guides.push({ type: 'vertical', x: d.x, x1: d.x, y1: Math.min(d.y, ny), x2: d.x, y2: Math.max(d.y, ny) })
          nx = d.x // snap to alignment
        }
        if (Math.abs(d.y - ny) < threshold) {
          guides.push({ type: 'horizontal', y: d.y, x1: Math.min(d.x, nx), y1: d.y, x2: Math.max(d.x, nx), y2: d.y })
          ny = d.y // snap to alignment
        }
      })
      setAlignmentGuides(guides)

      // Multi-select drag: move all selected devices together
      if (selectedIds.size > 1 && selectedIds.has(dragDevice)) {
        const draggedDev = devices.find(d => d.id === dragDevice)
        if (draggedDev) {
          const dx = nx - draggedDev.x
          const dy = ny - draggedDev.y
          setDevices(prev => prev.map(d =>
            selectedIds.has(d.id) ? { ...d, x: d.x + dx, y: d.y + dy } : d
          ))
        }
      } else {
        setDevices(prev => prev.map(d =>
          d.id === dragDevice ? { ...d, x: nx, y: ny } : d
        ))
      }
    }
  }, [isPanning, panStart, dragDevice, dragOffset, screenToCanvas, snapEnabled, calibrated, scale, devices, selectedIds, selectionBox])

  // ── AUTO-SAVE HELPER ──

  const showSaving = useCallback(async (fn: () => Promise<unknown>) => {
    setSaveStatus('saving')
    try {
      await fn()
      setSaveStatus('saved')
      setLastSaved(new Date())
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000)
    } catch {
      setSaveStatus('idle')
    }
  }, [])

  const dragStartPos = useRef<{ x: number; y: number } | null>(null)

  const handleMouseUp = useCallback(() => {
    // Finalize rubber band selection
    if (selectionBox && selectionStartRef.current) {
      const minX = Math.min(selectionBox.x1, selectionBox.x2)
      const maxX = Math.max(selectionBox.x1, selectionBox.x2)
      const minY = Math.min(selectionBox.y1, selectionBox.y2)
      const maxY = Math.max(selectionBox.y1, selectionBox.y2)
      const ids = new Set<string>()
      devices.forEach(d => {
        if (d.x >= minX && d.x <= maxX && d.y >= minY && d.y <= maxY) ids.add(d.id)
      })
      setSelectedIds(ids)
      setSelectionBox(null)
      selectionStartRef.current = null
      if (ids.size === 1) {
        setSelectedId([...ids][0])
        setShowProperties(true)
      }
      return
    }
    // Save position if we were dragging a device
    if (dragDevice) {
      const dev = devices.find(d => d.id === dragDevice)
      if (dev) {
        // Push undo with original position
        if (dragStartPos.current) {
          pushUndo({ type: 'move', deviceId: dev.id, previousPosition: dragStartPos.current })
          dragStartPos.current = null
        }
        if (dev.slug) {
          showSaving(() => api.patch(`${apiBase}/${slug}/devices/${dev.slug}`, { x: Math.round(dev.x), y: Math.round(dev.y) }))
        }
      }
    }
    setIsPanning(false)
    setDragDevice(null)
    setAlignmentGuides([])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragDevice, devices, showSaving, slug, selectionBox])

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (tool === TOOLS.PLACE && selectedCatalog) {
      const { x, y } = screenToCanvas(e.clientX, e.clientY)
      const cat = allItems.find(c => c.id === selectedCatalog)
      if (!cat) return
      trackRecentlyUsed(cat.id)
      // Use per-type auto-label generation
      setDevices(prev => {
        const label = generateLabel(cat.type, prev, labelConfig, plan?.floor_number)
        const tempId = `d${Date.now()}`
        const newDevice: CanvasDevice = {
          id: tempId, catalogId: cat.id, x, y, angle: 0,
          label, type: cat.type, icon: cat.icon,
          fov: cat.fov || 0, range: cat.range || 0, color: cat.color, name: cat.name,
          is_outdoor: false, ip_address: '', mac_address: '', vlan: '',
          idf_id: '', switch_device_id: '', switch_port: '',
          patch_panel_id: '', patch_port: '', mount_type: '',
          cable_type: '', cable_length_ft: null, install_status: 'Planned', installed_by: '',
        }

        // Push undo action
        pushUndo({ type: 'add', device: newDevice })

        // Persist to API with auto-save indicator
        if (plan) {
          showSaving(() =>
            api.post<PlacedDeviceType>(`${apiBase}/${slug}/devices`, {
              x: Math.round(x), y: Math.round(y), rotation: 0,
              label, device_type: cat.type,
              h_fov_degrees: cat.fov || null, max_range_ft: cat.range || null,
            }).then(eq => {
              setDevices(p => p.map(d => d.id === tempId ? { ...d, id: eq.id, slug: eq.slug } : d))
            })
          )
        }

        return [...prev, newDevice]
      })
    }
    if (tool === TOOLS.MEASURE) {
      const { x, y } = screenToCanvas(e.clientX, e.clientY)
      setMeasurePoints(prev => prev.length >= 2 ? [{ x, y }] : [...prev, { x, y }])
    }
    if (tool === TOOLS.CALIBRATE) {
      const { x, y } = screenToCanvas(e.clientX, e.clientY)
      setCalibPoints(prev => {
        if (prev.length >= 2) return [{ x, y }]
        const next = [...prev, { x, y }]
        if (next.length === 2) {
          setShowCalibModal(true)
          setCalibInput('')
        }
        return next
      })
    }
    if (tool === TOOLS.ROOM) {
      const { x, y } = screenToCanvas(e.clientX, e.clientY)
      // Check if clicking near first vertex to close shape
      if (roomVertices.length >= 3) {
        const first = roomVertices[0]
        const dist = Math.sqrt(Math.pow(x - first.x, 2) + Math.pow(y - first.y, 2))
        if (dist < 15) {
          // Close the room — show naming modal
          setShowRoomModal(true)
          setRoomNameInput('')
          return
        }
      }
      setRoomVertices(prev => [...prev, { x, y }])
    }
    if (tool === TOOLS.MDF) {
      const { x, y } = screenToCanvas(e.clientX, e.clientY)
      setMdfPendingPos({ x, y })
      setMdfType('idf')
      setMdfNameInput('IDF-01')
      setShowMdfModal(true)
    }
    if (tool === TOOLS.SELECT) {
      if (!(e.target as HTMLElement).closest('[data-device]')) {
        setSelectedId(null)
        setSelectedIds(new Set())
        setShowProperties(false)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, selectedCatalog, screenToCanvas, catalog, allItems, trackRecentlyUsed, plan, slug, labelConfig, roomVertices, showSaving])

  const handleDeviceClick = (e: React.MouseEvent, device: CanvasDevice) => {
    e.stopPropagation()
    if (tool === TOOLS.SELECT) {
      if (e.shiftKey) {
        // Multi-select: toggle device in selection set
        setSelectedIds(prev => {
          const next = new Set(prev)
          if (next.has(device.id)) next.delete(device.id)
          else next.add(device.id)
          return next
        })
        setSelectedId(device.id)
      } else {
        setSelectedIds(new Set())
        setSelectedId(device.id)
      }
      setShowProperties(true)
    }
    if (tool === TOOLS.CABLE) {
      if (!cableStart) {
        setCableStart(device.id)
      } else {
        if (cableStart !== device.id) {
          const fromDev = devices.find(d => d.id === cableStart)
          const toDev = device
          const cableType = CABLE_TYPE_DEFAULTS[toDev.type] || 'Plenum Cat6'
          const tempId = `cb${Date.now()}`
          const newCable: CanvasCable = {
            id: tempId, from: cableStart, to: device.id,
            type: cableType, color: CABLE_COLORS[cableType] || 'var(--color-cable)',
          }
          setCables(prev => [...prev, newCable])
          pushUndo({ type: 'cable_add', cable: newCable })

          // Calculate distance and persist to API
          if (plan && fromDev) {
            const pixelDist = Math.sqrt(Math.pow(toDev.x - fromDev.x, 2) + Math.pow(toDev.y - fromDev.y, 2))
            const estFeet = calibrated ? pixelDist / scale : undefined
            showSaving(() =>
              api.post<CableRunType>(`${apiBase}/${slug}/cables`, {
                from_device_id: fromDev.slug ? fromDev.id : undefined,
                to_device_id: toDev.slug ? toDev.id : undefined,
                cable_type: cableType,
                path: JSON.stringify([{ x: fromDev.x, y: fromDev.y }, { x: toDev.x, y: toDev.y }]),
                estimated_length_ft: estFeet ? Math.round(estFeet * 10) / 10 : undefined,
              }).then(saved => {
                setCables(prev => prev.map(c => c.id === tempId ? { ...c, id: saved.id, slug: saved.slug } : c))
              })
            )
          }
        }
        setCableStart(null)
      }
    }
  }

  const handleDeviceMouseDown = (e: React.MouseEvent, device: CanvasDevice) => {
    if (tool === TOOLS.SELECT) {
      e.stopPropagation()
      const { x, y } = screenToCanvas(e.clientX, e.clientY)
      dragStartPos.current = { x: device.x, y: device.y }
      setDragDevice(device.id)
      setDragOffset({ x: x - device.x, y: y - device.y })
      setSelectedId(device.id)
      setShowProperties(true)
    }
  }

  const deleteSelected = () => {
    if (!selectedId) return
    const dev = devices.find(d => d.id === selectedId)
    if (!dev) return
    if (!confirm(`Delete ${dev.label}? This will also remove any connected cables.`)) return
    pushUndo({ type: 'delete', device: dev })
    if (dev.slug) {
      showSaving(() => api.delete(`${apiBase}/${slug}/devices/${dev.slug}`))
    }
    // Also delete cables connected to this device
    cables.filter(c => c.from === selectedId || c.to === selectedId).forEach(c => {
      if (c.slug) {
        api.delete(`${apiBase}/${slug}/cables/${c.slug}`).catch(() => {})
      }
    })
    setDevices(prev => prev.filter(d => d.id !== selectedId))
    setCables(prev => prev.filter(c => c.from !== selectedId && c.to !== selectedId))
    setSelectedId(null)
    setShowProperties(false)
  }

  const deleteCable = (cableId: string) => {
    const cable = cables.find(c => c.id === cableId)
    if (!cable) return
    if (cable.slug) {
      showSaving(() => api.delete(`${apiBase}/${slug}/cables/${cable.slug}`))
    }
    pushUndo({ type: 'cable_delete', cable })
    setCables(prev => prev.filter(c => c.id !== cableId))
    setSelectedCableId(null)
  }

  // ── CALIBRATION APPLY ──

  const applyCalibration = () => {
    if (calibPoints.length !== 2 || !calibInput) return
    const realFeet = parseFloat(calibInput)
    if (!realFeet || realFeet <= 0) return
    const pixelDist = Math.sqrt(
      Math.pow(calibPoints[1].x - calibPoints[0].x, 2) +
      Math.pow(calibPoints[1].y - calibPoints[0].y, 2)
    )
    const newScale = pixelDist / realFeet
    setScale(newScale)
    setCalibrated(true)
    setShowCalibModal(false)
    setCalibPoints([])
    setTool(TOOLS.SELECT)
    // Persist to API
    if (plan) {
      const scaleData = isSitePlan
        ? { scale_px_per_ft: newScale }
        : { scale_px_per_ft: newScale, scale_calibrated: 1 }
      showSaving(() => api.patch(`${apiBase}/${slug}`, scaleData))
      setPlan(prev => prev ? { ...prev, scale_px_per_ft: newScale, scale_calibrated: 1 } : prev)
    }
  }

  // ── ROOM SAVE ──

  const saveRoom = () => {
    if (roomVertices.length < 3 || !roomNameInput.trim()) return
    const verts = [...roomVertices]
    const newRoom = { id: `room-${Date.now()}`, name: roomNameInput.trim(), vertices: verts, color: 'var(--accent)' }
    setRooms(prev => [...prev, newRoom])
    setRoomVertices([])
    setShowRoomModal(false)
    setTool(TOOLS.SELECT)
    // Persist to API
    if (plan) {
      showSaving(() =>
        api.post<FloorPlanRoomType>(`${apiBase}/${slug}/rooms`, {
          name: newRoom.name,
          polygon: JSON.stringify(verts),
          fill_color: '#c8850a20',
        }).then(saved => {
          setRooms(prev => prev.map(r => r.id === newRoom.id ? { ...r, id: saved.id, slug: saved.slug } : r))
        })
      )
    }
  }

  // ── MDF/IDF PLACE ──

  const placeMdf = () => {
    if (!mdfPendingPos) return
    const newInfra: CanvasInfra = {
      id: `${mdfType}-${Date.now()}`,
      x: mdfPendingPos.x, y: mdfPendingPos.y,
      type: mdfType, name: mdfNameInput.trim() || mdfType.toUpperCase(),
    }
    setInfra(prev => [...prev, newInfra])
    setShowMdfModal(false)
    setMdfPendingPos(null)
    setTool(TOOLS.SELECT)
    // Persist as device with type mdf/idf
    if (plan) {
      showSaving(() =>
        api.post<PlacedDeviceType>(`${apiBase}/${slug}/devices`, {
          x: Math.round(mdfPendingPos.x), y: Math.round(mdfPendingPos.y),
          rotation: 0, label: newInfra.name, device_type: mdfType,
        }).then(saved => {
          setInfra(prev => prev.map(i => i.id === newInfra.id ? { ...i, id: saved.id, slug: saved.slug } : i))
        })
      )
    }
  }

  // ── UNDO / REDO ──

  const pushUndo = (action: UndoAction) => {
    setUndoStack(prev => [...prev.slice(-49), action])
    setRedoStack([])
  }

  const performUndo = () => {
    if (undoStack.length === 0) return
    const action = undoStack[undoStack.length - 1]
    setUndoStack(prev => prev.slice(0, -1))
    setRedoStack(prev => [...prev, action])
    switch (action.type) {
      case 'add':
        if (action.device) {
          setDevices(prev => prev.filter(d => d.id !== action.device!.id))
          if (action.device.slug) api.delete(`${apiBase}/${slug}/devices/${action.device.slug}`).catch(() => {})
        }
        break
      case 'delete':
        if (action.device) setDevices(prev => [...prev, action.device!])
        break
      case 'move':
        if (action.deviceId && action.previousPosition) {
          setDevices(prev => prev.map(d =>
            d.id === action.deviceId ? { ...d, x: action.previousPosition!.x, y: action.previousPosition!.y } : d
          ))
        }
        break
      case 'cable_add':
        if (action.cable) {
          setCables(prev => prev.filter(c => c.id !== action.cable!.id))
          if (action.cable.slug) api.delete(`${apiBase}/${slug}/cables/${action.cable.slug}`).catch(() => {})
        }
        break
      case 'cable_delete':
        if (action.cable) setCables(prev => [...prev, action.cable!])
        break
    }
  }

  const performRedo = () => {
    if (redoStack.length === 0) return
    const action = redoStack[redoStack.length - 1]
    setRedoStack(prev => prev.slice(0, -1))
    setUndoStack(prev => [...prev, action])
    switch (action.type) {
      case 'add':
        if (action.device) setDevices(prev => [...prev, action.device!])
        break
      case 'delete':
        if (action.device) setDevices(prev => prev.filter(d => d.id !== action.device!.id))
        break
      case 'move':
        // move redo would need new position, skip for simplicity
        break
      case 'cable_add':
        if (action.cable) setCables(prev => [...prev, action.cable!])
        break
      case 'cable_delete':
        if (action.cable) setCables(prev => prev.filter(c => c.id !== action.cable!.id))
        break
    }
  }

  // ── CONTEXT MENU HANDLERS ──

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const deviceEl = (e.target as HTMLElement).closest('[data-device]')
    const deviceId = deviceEl?.getAttribute('data-device') || undefined
    setContextMenu({ x: e.clientX, y: e.clientY, deviceId })
  }, [])

  const closeContextMenu = () => setContextMenu(null)

  // ── LEGEND CLICK-TO-SELECT ──

  const legendSelectDevice = (device: CanvasDevice) => {
    setSelectedId(device.id)
    setShowProperties(true)
    // Pan canvas to center on device
    const svg = svgRef.current
    if (svg) {
      const rect = svg.getBoundingClientRect()
      setPan({ x: rect.width / 2 - device.x * zoom, y: rect.height / 2 - device.y * zoom })
    }
  }

  // ── KEYBOARD SHORTCUTS ──

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return
      // Copy/paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && !e.shiftKey) {
        e.preventDefault()
        const toCopy = selectedIds.size > 0
          ? devices.filter(d => selectedIds.has(d.id))
          : selectedId ? devices.filter(d => d.id === selectedId) : []
        if (toCopy.length > 0) clipboardRef.current = toCopy.map(d => ({ ...d }))
        return
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !e.shiftKey) {
        e.preventDefault()
        if (clipboardRef.current.length === 0) return
        const newDevs: CanvasDevice[] = clipboardRef.current.map(d => ({
          ...d,
          id: `d${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          slug: undefined,
          x: d.x + 20,
          y: d.y + 20,
          label: generateLabel(d.type, devices, labelConfig, plan?.floor_number),
          install_status: 'Planned',
        }))
        setDevices(prev => [...prev, ...newDevs])
        // Persist to API
        newDevs.forEach(nd => {
          if (plan) {
            showSaving(() =>
              api.post<PlacedDeviceType>(`${apiBase}/${slug}/devices`, {
                x: Math.round(nd.x), y: Math.round(nd.y), rotation: nd.angle,
                label: nd.label, device_type: nd.type,
                h_fov_degrees: nd.fov || null, max_range_ft: nd.range || null,
              }).then(eq => {
                setDevices(p => p.map(d => d.id === nd.id ? { ...d, id: eq.id, slug: eq.slug } : d))
              })
            )
          }
        })
        // Select the pasted devices
        setSelectedIds(new Set(newDevs.map(d => d.id)))
        return
      }
      // Save shortcut (Cmd+S / Ctrl+S)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (plan) {
          showSaving(() => api.patch(`${apiBase}/${slug}`, {
            canvas_width: plan.canvas_width || 1200,
            canvas_height: plan.canvas_height || 800,
          }))
        }
        return
      }
      // Undo/redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); performUndo(); return }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) { e.preventDefault(); performRedo(); return }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); performRedo(); return }
      if (e.ctrlKey || e.metaKey) return // handled above
      switch (e.key.toLowerCase()) {
        case 'v': setTool(TOOLS.SELECT); setSelectedCatalog(null); setCableStart(null); break
        case 'p': setTool(TOOLS.PLACE); setShowCatalog(true); break
        case 'c': setTool(TOOLS.CABLE); setCableStart(null); break
        case 'm': setTool(TOOLS.MEASURE); setMeasurePoints([]); break
        case 'r': setTool(TOOLS.ROOM); setRoomVertices([]); break
        case 'f': setTool(TOOLS.MDF); break
        case 'k': setTool(TOOLS.CALIBRATE); setCalibPoints([]); break
        case 'escape':
          setTool(TOOLS.SELECT); setSelectedCatalog(null); setCableStart(null)
          setMeasurePoints([]); setRoomVertices([]); setCalibPoints([])
          setContextMenu(null); setSelectedCableId(null); break
        case 'delete':
        case 'backspace':
          if (selectedCableId) { e.preventDefault(); deleteCable(selectedCableId) }
          else if (selectedId) { e.preventDefault(); deleteSelected() }
          break
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, devices, selectedIds, labelConfig])

  // ── LEGEND/LAYERS PERSISTENCE ──

  useEffect(() => {
    const saved = localStorage.getItem('fp-legend-state')
    if (saved) {
      try {
        const state = JSON.parse(saved)
        if (state.x != null) setLegendPos({ x: state.x, y: state.y })
        if (state.collapsed != null) setLegendCollapsed(state.collapsed)
        if (state.compact != null) setLegendCompact(state.compact)
        if (state.open != null) setShowLegend(state.open)
      } catch { /* ignore */ }
    }
    const savedLayers = localStorage.getItem('fp-layers-state')
    if (savedLayers) {
      try {
        const state = JSON.parse(savedLayers)
        if (state.x != null) setLayersPos({ x: state.x, y: state.y })
      } catch { /* ignore */ }
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('fp-legend-state', JSON.stringify({
      x: legendPos.x, y: legendPos.y, collapsed: legendCollapsed, compact: legendCompact, open: showLegend,
    }))
  }, [legendPos, legendCollapsed, legendCompact, showLegend])

  useEffect(() => {
    localStorage.setItem('fp-layers-state', JSON.stringify({ x: layersPos.x, y: layersPos.y }))
  }, [layersPos])

  // ── LEGEND/LAYERS DRAG HANDLERS ──

  const handleLegendDragMove = useCallback((e: MouseEvent) => {
    if (legendDragging) {
      setLegendPos({
        x: Math.max(0, Math.min(window.innerWidth - 240, e.clientX - legendDragOffset.current.x)),
        y: Math.max(44, Math.min(window.innerHeight - 100, e.clientY - legendDragOffset.current.y)),
      })
    }
    if (layersDragging) {
      setLayersPos({
        x: Math.max(0, Math.min(window.innerWidth - 200, e.clientX - layersDragOffset.current.x)),
        y: Math.max(44, Math.min(window.innerHeight - 100, e.clientY - layersDragOffset.current.y)),
      })
    }
  }, [legendDragging, layersDragging])

  const handleLegendDragUp = useCallback(() => {
    setLegendDragging(false)
    setLayersDragging(false)
  }, [])

  useEffect(() => {
    if (legendDragging || layersDragging) {
      window.addEventListener('mousemove', handleLegendDragMove)
      window.addEventListener('mouseup', handleLegendDragUp)
      return () => {
        window.removeEventListener('mousemove', handleLegendDragMove)
        window.removeEventListener('mouseup', handleLegendDragUp)
      }
    }
  }, [legendDragging, layersDragging, handleLegendDragMove, handleLegendDragUp])

  // ── BACKGROUND UPLOAD ──

  const handleUpload = async (file: File) => {
    if (!plan || !file) return
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    if (!isPdf && !file.type.startsWith('image/')) return

    // For PDFs, show the page selector instead of uploading directly
    if (isPdf) {
      setPdfFileForSelector(file)
      return
    }

    setUploading(true)
    try {
      // Auto-detect dimensions for image files
      const img = new Image()
      const objUrl = URL.createObjectURL(file)
      await new Promise<void>((resolve) => {
        img.onload = () => {
          const width = img.naturalWidth
          const height = img.naturalHeight
          URL.revokeObjectURL(objUrl)
          if (width && height) {
            const dims = isSitePlan
              ? { width_px: width, height_px: height }
              : { canvas_width: width, canvas_height: height }
            api.patch(`${apiBase}/${slug}`, dims).catch(() => {})
            setPlan(prev => prev ? { ...prev, canvas_width: width, canvas_height: height } : prev)
          }
          resolve()
        }
        img.onerror = () => { URL.revokeObjectURL(objUrl); resolve() }
        img.src = objUrl
      })

      const formData = new FormData()
      formData.append('file', file)
      const updated = await api.upload<FloorPlan & { width_px?: number; height_px?: number }>(`${apiBase}/${slug}/background`, formData)
      const serverWidth = updated.width_px || (updated as unknown as Record<string, unknown>).canvas_width as number
      const serverHeight = updated.height_px || (updated as unknown as Record<string, unknown>).canvas_height as number
      setPlan(prev => prev ? {
        ...prev,
        background_r2_key: updated.background_r2_key ?? prev.background_r2_key,
        canvas_width: serverWidth || prev.canvas_width,
        canvas_height: serverHeight || prev.canvas_height,
      } : prev)
    } catch { /* ignore */ }
    finally { setUploading(false) }
  }

  /** Called after user selects a page from PdfPageSelector — uploads the PNG conversion */
  const handlePdfPageSelected = async (pngBlob: Blob, _pageNumber: number, width: number, height: number) => {
    setPdfFileForSelector(null)
    if (!plan) return
    setUploading(true)
    try {
      const pngFile = new File([pngBlob], 'floor-plan.png', { type: 'image/png' })
      const formData = new FormData()
      formData.append('file', pngFile)
      const updated = await api.upload<FloorPlan & { width_px?: number; height_px?: number }>(`${apiBase}/${slug}/background`, formData)

      // Update dimensions
      const dims = isSitePlan
        ? { width_px: width, height_px: height }
        : { canvas_width: width, canvas_height: height }
      api.patch(`${apiBase}/${slug}`, dims).catch(() => {})

      const serverWidth = updated.width_px || width
      const serverHeight = updated.height_px || height
      setPlan(prev => prev ? {
        ...prev,
        background_r2_key: updated.background_r2_key ?? prev.background_r2_key,
        canvas_width: serverWidth || prev.canvas_width,
        canvas_height: serverHeight || prev.canvas_height,
      } : prev)
      setPdfConversionFailed(false)
    } catch { /* ignore */ }
    finally { setUploading(false) }
  }

  const backgroundUrl = plan?.background_r2_key ? `/api/files/${plan.background_r2_key}` : null
  const isPdfBackground = plan?.background_r2_key?.toLowerCase().endsWith('.pdf') ?? false

  // Legacy: if plan still has a .pdf background_r2_key, auto-convert page 1 client-side
  useEffect(() => {
    if (!isPdfBackground || !backgroundUrl || !plan) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetchAuthenticatedFile(backgroundUrl)
        if (cancelled) { URL.revokeObjectURL(res); return }
        // Fetch the blob and create a File for the page selector
        const blobRes = await fetch(res)
        const blob = await blobRes.blob()
        URL.revokeObjectURL(res)
        if (cancelled) return
        const file = new File([blob], 'legacy.pdf', { type: 'application/pdf' })
        setPdfFileForSelector(file)
      } catch (err) {
        console.error('[Legacy PDF load failed]', err)
      }
    })()
    return () => { cancelled = true }
  }, [isPdfBackground, backgroundUrl, plan])

  // ── Measurement ──
  const measureDist = measurePoints.length === 2
    ? Math.sqrt(Math.pow(measurePoints[1].x - measurePoints[0].x, 2) + Math.pow(measurePoints[1].y - measurePoints[0].y, 2))
    : 0
  const measureFeet = calibrated ? measureDist / scale : 0

  // ── EXPORT FUNCTIONS ──

  const exportAsPng = async () => {
    const svgElement = svgRef.current
    if (!svgElement) return

    // Clone the SVG so we don't modify the live one
    const clone = svgElement.cloneNode(true) as SVGSVGElement

    // Find background image and convert to base64 to avoid cross-origin canvas tainting
    const bgImage = clone.querySelector('image')
    if (bgImage) {
      const href = bgImage.getAttribute('href') || bgImage.getAttributeNS('http://www.w3.org/1999/xlink', 'href')
      if (href && !href.startsWith('data:')) {
        try {
          const response = await fetch(href)
          const blob = await response.blob()
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(blob)
          })
          bgImage.setAttribute('href', dataUrl)
          bgImage.setAttributeNS('http://www.w3.org/1999/xlink', 'href', dataUrl)
        } catch (err) {
          console.error('Failed to embed background image:', err)
        }
      }
    }

    // Set explicit dimensions on the clone
    const bbox = svgElement.getBBox()
    const exportW = Math.ceil(bbox.width + bbox.x + 100)
    const exportH = Math.ceil(bbox.height + bbox.y + 100)
    clone.setAttribute('width', String(exportW))
    clone.setAttribute('height', String(exportH))

    const svgData = new XMLSerializer().serializeToString(clone)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth * 2 // 2x for retina
      canvas.height = img.naturalHeight * 2
      const ctx = canvas.getContext('2d')!
      ctx.scale(2, 2)
      ctx.drawImage(img, 0, 0)

      const link = document.createElement('a')
      link.download = `${plan?.name || 'floorplan'}-export.png`
      link.href = canvas.toDataURL('image/png')
      link.click()

      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const downloadCsv = (filename: string, rows: string[][]) => {
    const csvContent = rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const link = document.createElement('a')
    link.download = filename
    link.href = URL.createObjectURL(blob)
    link.click()
  }

  const exportDeviceCsv = () => {
    const headers = ['Label', 'Name', 'Type', 'Indoor/Outdoor', 'IP', 'MAC', 'VLAN', 'IDF', 'Switch', 'Switch Port', 'Patch Panel', 'Patch Port', 'Mount', 'Cable Type', 'Cable Length', 'Status', 'X', 'Y', 'Floor']
    const rows = devices.map(d => {
      const idfName = infra.find(i => i.id === d.idf_id)?.name || ''
      const switchName = devices.find(s => s.id === d.switch_device_id)?.label || ''
      return [d.label, d.name, d.type, d.is_outdoor ? 'Outdoor' : 'Indoor', d.ip_address, d.mac_address, d.vlan, idfName, switchName, d.switch_port, '', d.patch_port, d.mount_type, d.cable_type, d.cable_length_ft?.toString() || '', d.install_status, Math.round(d.x).toString(), Math.round(d.y).toString(), plan?.floor_number?.toString() || '1']
    })
    downloadCsv(`${plan?.name || 'devices'}-device-list.csv`, [headers, ...rows])
  }

  const exportCableScheduleCsv = () => {
    const slackFactor = 1 + cableSlackPct / 100
    const headers = ['From Device', 'To Device', 'Cable Type', 'Estimated Length (ft)', 'From IDF', 'To IDF']
    const rows = cables.map(c => {
      const fromD = devices.find(d => d.id === c.from)
      const toD = devices.find(d => d.id === c.to)
      const pixelDist = fromD && toD ? Math.sqrt(Math.pow(toD.x - fromD.x, 2) + Math.pow(toD.y - fromD.y, 2)) : 0
      const estFeet = calibrated ? ((pixelDist / scale) * slackFactor).toFixed(0) : ''
      const fromIdf = fromD ? (infra.find(i => i.id === fromD.idf_id)?.name || '') : ''
      const toIdf = toD ? (infra.find(i => i.id === toD.idf_id)?.name || '') : ''
      return [fromD?.label || '', toD?.label || '', c.type, estFeet, fromIdf, toIdf]
    })
    downloadCsv(`${plan?.name || 'cables'}-cable-schedule.csv`, [headers, ...rows])
  }

  const exportPortMapCsv = () => {
    const headers = ['Device Label', 'Device Name', 'Device Type', 'Switch', 'Switch Port', 'Patch Panel', 'Patch Port', 'IP Address']
    const rows = devices.map(d => {
      const switchName = devices.find(s => s.id === d.switch_device_id)?.label || ''
      return [d.label, d.name, d.type, switchName, d.switch_port, '', d.patch_port, d.ip_address]
    })
    downloadCsv(`${plan?.name || 'portmap'}-port-map.csv`, [headers, ...rows])
  }

  // ── IMPORT FROM CSV ──

  const handleImportCsv = (file: File) => {
    const reader = new FileReader()
    reader.onload = (evt) => {
      const text = evt.target?.result as string
      if (!text) return
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
      if (lines.length < 2) return
      const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim().toLowerCase())
      const labelIdx = headers.findIndex(h => h === 'label')
      const typeIdx = headers.findIndex(h => h === 'type')
      const nameIdx = headers.findIndex(h => h === 'name')
      const ipIdx = headers.findIndex(h => h === 'ip')
      const macIdx = headers.findIndex(h => h === 'mac')
      const vlanIdx = headers.findIndex(h => h === 'vlan')
      const mountIdx = headers.findIndex(h => h.includes('mount'))
      const cableIdx = headers.findIndex(h => h.includes('cable type'))
      const statusIdx = headers.findIndex(h => h.includes('status'))

      if (labelIdx === -1 && typeIdx === -1) return // minimum required

      const newDevices: CanvasDevice[] = []
      const gridCols = Math.ceil(Math.sqrt(lines.length - 1))
      const centerX = (planW || 1200) / 2
      const centerY = (planH || 800) / 2

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.replace(/^"|"$/g, '').trim())
        const devLabel = cols[labelIdx] || `DEV-${i}`
        const devType = cols[typeIdx] || 'camera'
        const iconType = detectIconType({ category: devType })
        const devColor = colorForType(devType)
        const col = (i - 1) % gridCols
        const row = Math.floor((i - 1) / gridCols)
        const tempId = `imp-${Date.now()}-${i}`

        newDevices.push({
          id: tempId, catalogId: '', x: centerX - (gridCols * 40) / 2 + col * 40, y: centerY - (gridCols * 40) / 2 + row * 40,
          angle: 0, label: devLabel, name: nameIdx >= 0 ? cols[nameIdx] || devLabel : devLabel,
          type: devType, icon: iconType, fov: DEFAULT_FOV[iconType] || 0, range: DEFAULT_RANGE[iconType] || 0, color: devColor,
          is_outdoor: false,
          ip_address: ipIdx >= 0 ? cols[ipIdx] || '' : '',
          mac_address: macIdx >= 0 ? cols[macIdx] || '' : '',
          vlan: vlanIdx >= 0 ? cols[vlanIdx] || '' : '',
          idf_id: '', switch_device_id: '', switch_port: '',
          patch_panel_id: '', patch_port: '',
          mount_type: mountIdx >= 0 ? cols[mountIdx] || '' : '',
          cable_type: cableIdx >= 0 ? cols[cableIdx] || '' : '',
          cable_length_ft: null, install_status: statusIdx >= 0 ? cols[statusIdx] || 'Planned' : 'Planned', installed_by: '',
        })
      }

      setDevices(prev => [...prev, ...newDevices])
      // Persist each to API
      newDevices.forEach(nd => {
        if (plan) {
          showSaving(() =>
            api.post<PlacedDeviceType>(`${apiBase}/${slug}/devices`, {
              x: Math.round(nd.x), y: Math.round(nd.y), rotation: 0,
              label: nd.label, device_type: nd.type,
            }).then(eq => {
              setDevices(p => p.map(d => d.id === nd.id ? { ...d, id: eq.id, slug: eq.slug } : d))
            })
          )
        }
      })
    }
    reader.readAsText(file)
  }

  // ── LOADING / ERROR ──

  if (loading) return (
    <div className="fp-root" style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading plan...</div>
    </div>
  )
  if (error || !plan) return (
    <div className="fp-root" style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error || 'Plan not found'}</div>
      <button className="fp-back-btn" style={{ marginTop: 12 }} onClick={() => navigate(-1)}>Go Back</button>
    </div>
  )

  const planW = plan.canvas_width || 1200
  const planH = plan.canvas_height || 800

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <div className="fp-root" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      {/* ── TOP BAR ── */}
      <div className="fp-topbar">
        <div className="fp-topbar-left">
          <button className="fp-back-btn" onClick={() => navigate(-1)}>{'\u2190'} Back</button>
          <span className="fp-plan-name">{plan.name}</span>
          <span style={{ fontSize: 12, color: saveStatus === 'saving' ? 'var(--accent)' : 'var(--text-3)', fontFamily: 'var(--mono)', display: 'flex', alignItems: 'center', gap: 6 }}>
            {saveStatus === 'saving' ? 'Saving...' : lastSaved ? `Saved ${Math.round((Date.now() - lastSaved.getTime()) / 1000)}s ago` : ''}
          </span>
          <span className="fp-plan-meta">{planW} {'\u00D7'} {planH}</span>
          {calibrated ? (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999, background: 'var(--ok-m)', color: 'var(--ok)' }}>
              1px = {(1 / scale).toFixed(2)}ft
            </span>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999, background: 'var(--warn-m)', color: 'var(--warn)' }}>
              Not calibrated
            </span>
          )}
        </div>
        <div className="fp-topbar-center">
          <div className="fp-floor-tabs">
            {floors.map(f => (
              <button
                key={f.id}
                className={`fp-floor-tab ${activeFloor === f.id ? 'active' : ''}`}
                onClick={() => { setActiveFloor(f.id); if (f.slug !== slug) navigate(`${isSitePlan ? '/site-plans' : '/floor-plans'}/${f.slug}`) }}
              >
                {f.name}
              </button>
            ))}
          </div>
        </div>
        <div className="fp-topbar-right">
          <div style={{ position: 'relative' }}>
            <button className="fp-top-action" onClick={() => setShowExportMenu(v => !v)}>
              {'\u2913'} Export
            </button>
            {showExportMenu && (
              <div className="fp-context-menu" style={{ position: 'absolute', top: '100%', right: 0, minWidth: 200 }}>
                <button className="fp-ctx-item" onClick={() => { exportAsPng(); setShowExportMenu(false) }}>Floorplan as PNG</button>
                <button className="fp-ctx-item" onClick={() => { exportDeviceCsv(); setShowExportMenu(false) }}>Device List (CSV)</button>
                <button className="fp-ctx-item" onClick={() => { exportCableScheduleCsv(); setShowExportMenu(false) }}>Cable Schedule (CSV)</button>
                <button className="fp-ctx-item" onClick={() => { exportPortMapCsv(); setShowExportMenu(false) }}>Port Map (CSV)</button>
              </div>
            )}
          </div>
          {scan3dUrl && (
            <button className="fp-top-action" onClick={() => window.open(scan3dUrl, '_blank')}>
              3D Scan
            </button>
          )}
          <button className="fp-top-action" onClick={() => setShowScan3dModal(true)} title="3D Scan Settings">
            {'\u2316'}
          </button>
          <button className="fp-top-action" onClick={() => importFileRef.current?.click()}>
            {'\u2912'} Import
          </button>
          <input ref={importFileRef} type="file" accept=".csv" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleImportCsv(f); e.target.value = '' }} />
          <button className="fp-top-action" onClick={() => setShowLabelConfig(true)} title="Label Settings">
            {'\u2699'} Labels
          </button>
          <button className="fp-top-action" onClick={() => setShowLegend(!showLegend)}>
            {showLegend ? '\u2715 Legend' : '\u2630 Legend'}
          </button>
          <button className="fp-top-action" onClick={() => setShowLayers(!showLayers)}>
            {showLayers ? '\u2715 Layers' : '\u25D0 Layers'}
          </button>
          {saveStatus === 'saving' && <span className="fp-save-indicator">Saving...</span>}
          {saveStatus === 'saved' && <span className="fp-save-indicator saved">Saved {'\u2713'}</span>}
          <span className="fp-zoom-label">{Math.round(zoom * 100)}%</span>
        </div>
      </div>

      {/* ── MAIN AREA ── */}
      <div className="fp-main-area">
        {/* ── CATALOG PANEL (LEFT) ── */}
        {showCatalog && (() => {
          const searchLower = catalogSearch.toLowerCase()
          const matchesSearch = (item: PlaceableCatalogItem) =>
            !catalogSearch || item.name.toLowerCase().includes(searchLower) ||
            item.type.toLowerCase().includes(searchLower) ||
            item.brand.toLowerCase().includes(searchLower)
          const matchesFilter = (item: PlaceableCatalogItem) =>
            catFilter === 'all' || item.type === catFilter
          const favoriteItems = allItems.filter(i => favorites.includes(i.id) && matchesSearch(i))
          const recentItems = recentlyUsed
            .map(id => allItems.find(i => i.id === id))
            .filter((i): i is PlaceableCatalogItem => !!i && matchesSearch(i))
            .slice(0, 10)
          const filteredGeneric = GENERIC_DEVICES.filter(i => matchesSearch(i) && matchesFilter(i))
          const filteredCatalog = catalog.filter(i => matchesSearch(i) && matchesFilter(i))
          // Group catalog items by type for collapsible categories
          const catalogByType = new Map<string, PlaceableCatalogItem[]>()
          filteredCatalog.forEach(item => {
            const key = item.type || 'other'
            if (!catalogByType.has(key)) catalogByType.set(key, [])
            catalogByType.get(key)!.push(item)
          })

          const renderGridItem = (item: PlaceableCatalogItem) => (
            <div
              key={item.id}
              onClick={() => { setSelectedCatalog(item.id); setTool(TOOLS.PLACE) }}
              className={`fp-eq-grid-item ${selectedCatalog === item.id ? 'active' : ''}`}
            >
              <button
                className={`fp-eq-star ${favorites.includes(item.id) ? 'starred' : ''}`}
                onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id) }}
                title={favorites.includes(item.id) ? 'Remove from favorites' : 'Add to favorites'}
              >
                {favorites.includes(item.id) ? '\u2605' : '\u2606'}
              </button>
              <svg width={24} height={24} viewBox="0 0 28 28"><DeviceIcon type={item.icon} size={28} color={item.color} /></svg>
              <span className="fp-eq-grid-name">{item.name}</span>
            </div>
          )

          const renderSectionHeader = (label: string, section: string, count: number) => (
            <button
              className="fp-eq-section-header"
              onClick={() => togglePanelSection(section)}
            >
              <span className="fp-eq-section-chevron">{collapsedPanelSections.has(section) ? '\u25B6' : '\u25BC'}</span>
              <span className="fp-eq-section-label">{label}</span>
              <span className="fp-eq-section-count">{count}</span>
            </button>
          )

          return (
          <div className="fp-catalog-panel" style={{ width: 264 }}>
            <div className="fp-catalog-header">
              <span className="fp-catalog-title">Equipment</span>
              <button className="fp-catalog-close" onClick={() => setShowCatalog(false)}>{'\u2715'}</button>
            </div>
            {/* Search */}
            <div style={{ padding: '8px 8px 4px' }}>
              <input
                type="text"
                value={catalogSearch}
                onChange={e => setCatalogSearch(e.target.value)}
                placeholder="Search equipment..."
                className="fp-eq-search"
              />
            </div>
            {/* Category filters */}
            <div className="fp-cat-filters">
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setCatFilter(c.id)}
                  className={`fp-cat-btn ${catFilter === c.id ? 'active' : ''}`}
                >
                  <span>{c.icon}</span>
                  <span>{c.label}</span>
                </button>
              ))}
            </div>
            {/* Scrollable content */}
            <div className="fp-catalog-list">
              {/* Favorites section */}
              {favoriteItems.length > 0 && (
                <div className="fp-eq-section">
                  {renderSectionHeader('Favorites', 'favorites', favoriteItems.length)}
                  {!collapsedPanelSections.has('favorites') && (
                    <div className="fp-eq-grid">{favoriteItems.map(renderGridItem)}</div>
                  )}
                </div>
              )}

              {/* Recently used section */}
              {recentItems.length > 0 && (
                <div className="fp-eq-section">
                  {renderSectionHeader('Recently Used', 'recent', recentItems.length)}
                  {!collapsedPanelSections.has('recent') && (
                    <div className="fp-eq-grid">{recentItems.map(renderGridItem)}</div>
                  )}
                </div>
              )}

              {/* Generic devices section */}
              {filteredGeneric.length > 0 && (
                <div className="fp-eq-section">
                  {renderSectionHeader('Generic Devices', 'generic', filteredGeneric.length)}
                  {!collapsedPanelSections.has('generic') && (
                    <div className="fp-eq-grid">{filteredGeneric.map(renderGridItem)}</div>
                  )}
                </div>
              )}

              {/* Catalog items by category */}
              {catalogLoading && (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--fp-text-muted)', fontSize: 12 }}>Loading catalog...</div>
              )}
              {!catalogLoading && filteredCatalog.length === 0 && filteredGeneric.length === 0 && favoriteItems.length === 0 && recentItems.length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--fp-text-muted)', fontSize: 12 }}>No equipment found</div>
              )}
              {!catalogLoading && Array.from(catalogByType.entries()).map(([typeKey, items]) => {
                const catDef = CATEGORIES.find(c => c.id === typeKey)
                const label = catDef?.label || typeKey.charAt(0).toUpperCase() + typeKey.slice(1)
                const sectionKey = `cat-${typeKey}`
                return (
                  <div key={typeKey} className="fp-eq-section">
                    {renderSectionHeader(label, sectionKey, items.length)}
                    {!collapsedPanelSections.has(sectionKey) && (
                      <div className="fp-eq-grid">{items.map(renderGridItem)}</div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
          )
        })()}

        {/* ── CANVAS ── */}
        <div className="fp-canvas-container">
          {/* Toolbar */}
          <div className="fp-toolbar">
            <ToolBtn icon={'\u2196'} label="Select" active={tool === TOOLS.SELECT} onClick={() => { setTool(TOOLS.SELECT); setSelectedCatalog(null); setCableStart(null) }} />
            <ToolBtn icon={'\u25C9'} label="Place" active={tool === TOOLS.PLACE} onClick={() => { setTool(TOOLS.PLACE); if (!showCatalog) setShowCatalog(true) }} />
            <div className="fp-tool-divider" />
            <ToolBtn icon={'\u2307'} label="Cable" active={tool === TOOLS.CABLE} onClick={() => { setTool(TOOLS.CABLE); setCableStart(null) }} />
            <ToolBtn icon={'\u2194'} label="Measure" active={tool === TOOLS.MEASURE} onClick={() => { setTool(TOOLS.MEASURE); setMeasurePoints([]) }} />
            <ToolBtn icon={'\u25A2'} label="Room" active={tool === TOOLS.ROOM} onClick={() => setTool(TOOLS.ROOM)} />
            <div className="fp-tool-divider" />
            <ToolBtn icon={'\u229E'} label="MDF" active={tool === TOOLS.MDF} onClick={() => setTool(TOOLS.MDF)} />
            <ToolBtn icon={'\u22A1'} label="Calibrate" active={tool === TOOLS.CALIBRATE} onClick={() => setTool(TOOLS.CALIBRATE)} />
            <div className="fp-tool-divider" />
            <ToolBtn icon={'\u229E'} label="Snap" active={snapEnabled} onClick={() => setSnapEnabled(s => !s)} />
            <ToolBtn icon={'\u2212'} onClick={() => setZoom(z => Math.max(0.2, z * 0.8))} />
            <ToolBtn icon="+" onClick={() => setZoom(z => Math.min(5, z * 1.25))} />
            <ToolBtn icon={'\u2299'} onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }) }} />
          </div>

          {/* Status bar */}
          {tool === TOOLS.PLACE && selectedCatalog && (
            <div className="fp-status-bar">
              Click on the plan to place <b>{allItems.find(c => c.id === selectedCatalog)?.name}</b>. Press ESC to cancel.
            </div>
          )}
          {tool === TOOLS.CABLE && (
            <div className="fp-status-bar">
              {cableStart ? 'Click a second device to complete the cable run.' : 'Click a device to start a cable run.'}
            </div>
          )}
          {tool === TOOLS.MEASURE && (
            <div className="fp-status-bar">
              Click two points to measure distance.
              {measurePoints.length === 2 && (
                <span style={{ marginLeft: 12, fontWeight: 700, fontFamily: "var(--fp-mono)" }}>
                  {fmt(measureDist)}px{calibrated ? ` = ${fmt(measureFeet)}ft (${fmt(measureFeet * 0.3048)}m)` : ''}
                </span>
              )}
            </div>
          )}
          {tool === TOOLS.CALIBRATE && (
            <div className="fp-status-bar">
              {calibPoints.length === 0 && 'Click two points with a known distance, then enter the real distance.'}
              {calibPoints.length === 1 && 'Click a second point to complete calibration.'}
              {calibPoints.length === 2 && 'Enter the known distance in the dialog.'}
            </div>
          )}
          {tool === TOOLS.ROOM && (
            <div className="fp-status-bar">
              Click to place room corners. Double-click or close the shape to finish.
              {roomVertices.length > 0 && <span style={{ marginLeft: 8, color: 'var(--accent)' }}>{roomVertices.length} points</span>}
            </div>
          )}
          {tool === TOOLS.MDF && (
            <div className="fp-status-bar">Click on the plan to place an MDF/IDF point.</div>
          )}

          {/* PDF page selector modal */}
          {pdfFileForSelector && (
            <Suspense fallback={null}>
              <PdfPageSelector
                file={pdfFileForSelector}
                onCancel={() => setPdfFileForSelector(null)}
                onSelect={handlePdfPageSelected}
              />
            </Suspense>
          )}

          {/* Upload prompt when no background */}
          {!backgroundUrl && !uploading && (
            <div
              className={`fp-upload-prompt ${dragover ? 'dragover' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragover(true) }}
              onDragLeave={() => setDragover(false)}
              onDrop={e => { e.preventDefault(); setDragover(false); const f = e.dataTransfer.files[0]; if (f) handleUpload(f) }}
            >
              <div className="fp-upload-icon">{'\uD83D\uDCC4'}</div>
              <div className="fp-upload-title">Upload Floor Plan</div>
              <div className="fp-upload-sub">Drag & drop an image or click to browse</div>
              <div className="fp-upload-types">Supported: PDF, PNG, JPG</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f) }}
              />
            </div>
          )}
          {uploading && (
            <div className="fp-upload-prompt">
              <div className="fp-upload-title">Uploading...</div>
            </div>
          )}

          {/* SVG Canvas */}
          <svg
            ref={svgRef}
            className={`fp-svg ${isPanning ? 'panning' : ''}`}
            onMouseDown={handleMouseDown}
            onClick={(e) => { closeContextMenu(); handleCanvasClick(e) }}
            onContextMenu={handleContextMenu}
            onDoubleClick={() => {
              if (tool === TOOLS.ROOM && roomVertices.length >= 3) {
                setShowRoomModal(true)
                setRoomNameInput('')
              }
            }}
          >
            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              {/* Background grid */}
              <defs>
                <pattern id="fp-grid" width={40} height={40} patternUnits="userSpaceOnUse">
                  <circle cx={20} cy={20} r={0.8} fill="var(--border-h)" />
                </pattern>
              </defs>
              <rect x={-2000} y={-2000} width={6000} height={4000} fill="var(--bg-3)" />
              <rect x={-2000} y={-2000} width={6000} height={4000} fill="url(#fp-grid)" />

              {/* Background image */}
              {backgroundUrl && !isPdfBackground && (
                <image href={backgroundUrl} x={0} y={0} width={planW} height={planH} />
              )}

              {/* Plan outline when no background */}
              {!backgroundUrl && (
                <rect x={40} y={40} width={planW - 80} height={planH - 80} fill="none" stroke="var(--bg-4)" strokeWidth={2} />
              )}

              {/* Rooms */}
              {rooms.map(room => {
                const center = room.vertices.length > 0
                  ? { x: room.vertices.reduce((s, v) => s + v.x, 0) / room.vertices.length, y: room.vertices.reduce((s, v) => s + v.y, 0) / room.vertices.length }
                  : { x: 0, y: 0 }
                return (
                  <g key={room.id}>
                    <polygon
                      points={room.vertices.map(v => `${v.x},${v.y}`).join(' ')}
                      fill={room.color || 'var(--accent)'} fillOpacity={0.04}
                      stroke={room.color || 'var(--accent)'} strokeOpacity={0.2} strokeWidth={1}
                    />
                    <text x={center.x} y={center.y} textAnchor="middle" fill="var(--text-tertiary)" fontSize={12} fontWeight={600}>
                      {room.name}
                    </text>
                  </g>
                )
              })}

              {/* Room drawing in progress */}
              {roomVertices.length > 0 && (
                <g>
                  <polyline
                    points={roomVertices.map(v => `${v.x},${v.y}`).join(' ')}
                    fill="none" stroke="var(--accent)" strokeWidth={1.5} strokeDasharray="5 3"
                  />
                  {roomVertices.map((v, i) => (
                    <circle key={i} cx={v.x} cy={v.y} r={4} fill={i === 0 ? 'var(--accent)' : 'var(--accent-m)'} />
                  ))}
                </g>
              )}

              {/* Calibration points */}
              {calibPoints.map((p, i) => (
                <circle key={`cal-${i}`} cx={p.x} cy={p.y} r={5} fill="var(--ok)" stroke="var(--ok)" strokeWidth={2}>
                  {i === 0 && calibPoints.length === 1 && (
                    <animate attributeName="r" values="5;7;5" dur="1s" repeatCount="indefinite" />
                  )}
                </circle>
              ))}
              {calibPoints.length === 2 && (
                <line x1={calibPoints[0].x} y1={calibPoints[0].y} x2={calibPoints[1].x} y2={calibPoints[1].y}
                  stroke="var(--ok)" strokeWidth={1.5} strokeDasharray="5 3" />
              )}

              {/* Cable runs */}
              {layers.find(l => l.id === 'cables')?.visible && cables.map(cable => {
                const fromD = devices.find(d => d.id === cable.from)
                const toD = devices.find(d => d.id === cable.to)
                if (!fromD || !toD) return null
                const midX = (fromD.x + toD.x) / 2
                const midY = (fromD.y + toD.y) / 2
                const pixelDist = Math.sqrt(Math.pow(toD.x - fromD.x, 2) + Math.pow(toD.y - fromD.y, 2))
                const slackFactor = 1 + cableSlackPct / 100
                const estFeet = calibrated ? (pixelDist / scale) * slackFactor : 0
                const isSelected = selectedCableId === cable.id
                return (
                  <g key={cable.id} style={{ cursor: 'pointer' }}
                    onClick={(ev) => { ev.stopPropagation(); setSelectedCableId(isSelected ? null : cable.id); setSelectedId(null) }}>
                    {/* Wider invisible hit area for easier clicking */}
                    <line x1={fromD.x} y1={fromD.y} x2={toD.x} y2={toD.y}
                      stroke="transparent" strokeWidth={12} />
                    {/* Visible cable line */}
                    <line x1={fromD.x} y1={fromD.y} x2={toD.x} y2={toD.y}
                      stroke={isSelected ? 'var(--accent)' : cable.color} strokeWidth={isSelected ? 2.5 : 1.5} strokeDasharray="6 3" opacity={isSelected ? 1 : 0.5} />
                    {/* Cable type label */}
                    <text x={midX} y={midY - 6} textAnchor="middle" fill={isSelected ? 'var(--accent)' : 'var(--color-cable)'} fontSize={9}
                      fontFamily="var(--mono, 'JetBrains Mono', monospace)" opacity={isSelected ? 1 : 0.7}>
                      {cable.type}{calibrated && estFeet > 0 ? ` · ${estFeet.toFixed(0)}ft` : ''}
                    </text>
                    {/* Delete button on selected cable */}
                    {isSelected && (
                      <g onClick={(ev) => { ev.stopPropagation(); deleteCable(cable.id) }} style={{ cursor: 'pointer' }}>
                        <circle cx={midX + 20} cy={midY - 6} r={8} fill="var(--err)" opacity={0.9} />
                        <text x={midX + 20} y={midY - 2} textAnchor="middle" fill="white" fontSize={10} fontWeight={700}>x</text>
                      </g>
                    )}
                  </g>
                )
              })}

              {/* FOV Cones */}
              {devices.filter(d => d.type === 'camera' && d.fov > 0 && isLayerVisible(d.type)).map(d => (
                <FovCone key={`fov-${d.id}`} x={d.x} y={d.y} angle={d.angle} fov={d.fov} range={d.range} color={d.color} />
              ))}

              {/* Infrastructure */}
              {infra.map(inf => (
                <g key={inf.id} transform={`translate(${inf.x - 16},${inf.y - 16})`}>
                  <svg width={32} height={32} viewBox="0 0 28 28"><DeviceIcon type={inf.type} size={28} /></svg>
                  <text x={16} y={-4} textAnchor="middle" fill="var(--text-secondary)" fontSize={9} fontWeight={700}>{inf.name}</text>
                </g>
              ))}

              {/* Devices */}
              {devices.filter(d => isLayerVisible(d.type)).map((d, i) => (
                <g
                  key={d.id}
                  data-device={d.id}
                  transform={`translate(${d.x - 14},${d.y - 14})`}
                  style={{ cursor: tool === TOOLS.SELECT ? 'grab' : 'pointer' }}
                  onClick={e => handleDeviceClick(e, d)}
                  onMouseDown={e => handleDeviceMouseDown(e, d)}
                >
                  {/* Selection ring */}
                  {(selectedId === d.id || selectedIds.has(d.id)) && (
                    <circle cx={14} cy={14} r={20} fill="none" stroke="var(--accent)" strokeWidth={2} strokeDasharray="4 3">
                      <animateTransform attributeName="transform" type="rotate" from="0 14 14" to="360 14 14" dur="8s" repeatCount="indefinite" />
                    </circle>
                  )}
                  {/* Cable start indicator */}
                  {cableStart === d.id && (
                    <circle cx={14} cy={14} r={20} fill="none" stroke="var(--color-cable)" strokeWidth={2}>
                      <animate attributeName="r" values="18;22;18" dur="1s" repeatCount="indefinite" />
                    </circle>
                  )}
                  {/* Device icon */}
                  <svg width={28} height={28} viewBox="0 0 28 28"><DeviceIcon type={d.icon} size={28} color={d.color} /></svg>
                  {/* Label */}
                  <text x={14} y={-6} textAnchor="middle" fill="var(--text-primary)" fontSize={8} fontWeight={700} letterSpacing={0.5}>{d.label}</text>
                  {/* Number badge */}
                  <circle cx={26} cy={2} r={7} fill="var(--bg-3)" stroke={d.color} strokeWidth={1.5} />
                  <text x={26} y={5} textAnchor="middle" fill={d.color} fontSize={7} fontWeight={800}>{i + 1}</text>
                </g>
              ))}

              {/* Measurement line */}
              {measurePoints.length === 2 && layers.find(l => l.id === 'measurements')?.visible && (
                <g>
                  <line x1={measurePoints[0].x} y1={measurePoints[0].y} x2={measurePoints[1].x} y2={measurePoints[1].y}
                    stroke="#ec4899" strokeWidth={1.5} strokeDasharray="5 3" />
                  <circle cx={measurePoints[0].x} cy={measurePoints[0].y} r={4} fill="#ec4899" />
                  <circle cx={measurePoints[1].x} cy={measurePoints[1].y} r={4} fill="#ec4899" />
                  <text
                    x={(measurePoints[0].x + measurePoints[1].x) / 2}
                    y={(measurePoints[0].y + measurePoints[1].y) / 2 - 10}
                    textAnchor="middle" fill="#ec4899" fontSize={11} fontWeight={700}
                    fontFamily="'JetBrains Mono', monospace"
                  >
                    {calibrated ? `${fmt(measureFeet)}ft` : `${fmt(measureDist)}px`}
                  </text>
                </g>
              )}
              {measurePoints.length === 1 && (
                <circle cx={measurePoints[0].x} cy={measurePoints[0].y} r={4} fill="#ec4899">
                  <animate attributeName="r" values="4;6;4" dur="1s" repeatCount="indefinite" />
                </circle>
              )}

              {/* Alignment guides */}
              {alignmentGuides.map((g, i) => (
                <line key={`guide-${i}`} x1={g.x1} y1={g.y1} x2={g.x2} y2={g.y2}
                  stroke="#22d3ee" strokeWidth={0.5} strokeDasharray="4 3" opacity={0.6} />
              ))}

              {/* Rubber band selection box */}
              {selectionBox && (
                <rect
                  x={Math.min(selectionBox.x1, selectionBox.x2)} y={Math.min(selectionBox.y1, selectionBox.y2)}
                  width={Math.abs(selectionBox.x2 - selectionBox.x1)} height={Math.abs(selectionBox.y2 - selectionBox.y1)}
                  fill="var(--accent)" fillOpacity={0.08} stroke="var(--accent)" strokeWidth={1} strokeDasharray="5 3"
                />
              )}
            </g>
          </svg>

          {/* Keyboard hints */}
          <div className="fp-hints">
            <span className="fp-hint"><kbd className="fp-kbd">V</kbd> Select</span>
            <span className="fp-hint"><kbd className="fp-kbd">C</kbd> Cable</span>
            <span className="fp-hint"><kbd className="fp-kbd">M</kbd> Measure</span>
            <span className="fp-hint"><kbd className="fp-kbd">R</kbd> Room</span>
            <span className="fp-hint"><kbd className="fp-kbd">K</kbd> Calibrate</span>
            <span className="fp-hint"><kbd className="fp-kbd">Ctrl+Z</kbd> Undo</span>
            <span className="fp-hint"><kbd className="fp-kbd">Scroll</kbd> Zoom</span>
            <span className="fp-hint"><kbd className="fp-kbd">Del</kbd> Delete</span>
          </div>
        </div>

        {/* ── BULK PROPERTIES PANEL (multi-select) ── */}
        {selectedIds.size > 1 && (
          <div className="fp-props-panel">
            <div className="fp-props-header">
              <span className="fp-props-title">{selectedIds.size} devices selected</span>
              <button className="fp-props-close" onClick={() => { setSelectedIds(new Set()); setShowProperties(false) }}>{'\u2715'}</button>
            </div>
            <div className="fp-props-section" style={{ marginTop: 8 }}>
              <div className="fp-props-field" style={{ marginBottom: 6 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Set Cable Type</label>
                <select className="fp-props-input" defaultValue="" onChange={e => {
                  const val = e.target.value
                  if (!val) return
                  setDevices(prev => prev.map(d => selectedIds.has(d.id) ? { ...d, cable_type: val } : d))
                  devices.filter(d => selectedIds.has(d.id) && d.slug).forEach(d => {
                    showSaving(() => api.patch(`${apiBase}/${slug}/devices/${d.slug}`, { cable_type: val }))
                  })
                }}>
                  <option value="">—</option>
                  {CABLE_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                </select>
              </div>
              <div className="fp-props-field" style={{ marginBottom: 6 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Set IDF</label>
                <select className="fp-props-input" defaultValue="" onChange={e => {
                  const val = e.target.value
                  if (!val) return
                  setDevices(prev => prev.map(d => selectedIds.has(d.id) ? { ...d, idf_id: val } : d))
                  devices.filter(d => selectedIds.has(d.id) && d.slug).forEach(d => {
                    showSaving(() => api.patch(`${apiBase}/${slug}/devices/${d.slug}`, { idf_id: val }))
                  })
                }}>
                  <option value="">—</option>
                  {infra.filter(i => i.type === 'mdf' || i.type === 'idf').map(i => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>
              <div className="fp-props-field" style={{ marginBottom: 6 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Set Status</label>
                <select className="fp-props-input" defaultValue="" onChange={e => {
                  const val = e.target.value
                  if (!val) return
                  setDevices(prev => prev.map(d => selectedIds.has(d.id) ? { ...d, install_status: val } : d))
                  devices.filter(d => selectedIds.has(d.id) && d.slug).forEach(d => {
                    showSaving(() => api.patch(`${apiBase}/${slug}/devices/${d.slug}`, { install_status: val }))
                  })
                }}>
                  <option value="">—</option>
                  {INSTALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: 'auto', paddingTop: 16 }}>
              <button className="fp-props-delete-btn" onClick={() => {
                if (!confirm(`Delete ${selectedIds.size} devices?`)) return
                const ids = [...selectedIds]
                ids.forEach(id => {
                  const dev = devices.find(d => d.id === id)
                  if (dev?.slug) api.delete(`${apiBase}/${slug}/devices/${dev.slug}`).catch(() => {})
                })
                setDevices(prev => prev.filter(d => !selectedIds.has(d.id)))
                setCables(prev => prev.filter(c => !selectedIds.has(c.from) && !selectedIds.has(c.to)))
                setSelectedIds(new Set())
                setSelectedId(null)
              }}>Delete {selectedIds.size} Devices</button>
            </div>
          </div>
        )}

        {/* ── PROPERTIES PANEL (RIGHT) ── */}
        {showProperties && selected && selectedIds.size <= 1 && (
          <div className="fp-props-panel">
            <div className="fp-props-header">
              <span className="fp-props-title">Properties</span>
              <button className="fp-props-close" onClick={() => setShowProperties(false)}>{'\u2715'}</button>
            </div>

            <div className="fp-props-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <svg width={36} height={36} viewBox="0 0 28 28"><DeviceIcon type={selected.icon} size={28} color={selected.color} /></svg>
                <div>
                  <div className="fp-props-dev-name">{selected.name}</div>
                  <div className="fp-props-dev-type">{selected.label}</div>
                </div>
              </div>
            </div>

            <div className="fp-props-field">
              <label className="fp-props-label">Label</label>
              <input
                className="fp-props-input"
                value={selected.label}
                onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, label: e.target.value } : d))}
                onBlur={() => {
                  if (selected.slug) {
                    showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { label: selected.label }))
                  }
                }}
              />
            </div>

            <div className="fp-props-field">
              <label className="fp-props-label">Name</label>
              <input
                className="fp-props-input"
                value={selected.name}
                onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, name: e.target.value } : d))}
                onBlur={() => {
                  if (selected.slug) {
                    showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { label: selected.name }))
                  }
                }}
              />
            </div>

            {/* Feature 3: Indoor/Outdoor */}
            <div className="fp-props-field">
              <label className="fp-props-label">Location</label>
              <div style={{ display: 'flex', gap: 12 }}>
                {[false, true].map(outdoor => (
                  <label key={String(outdoor)} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <input
                      type="radio" name="outdoor" checked={selected.is_outdoor === outdoor}
                      onChange={() => {
                        setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, is_outdoor: outdoor } : d))
                        if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { is_outdoor: outdoor ? 1 : 0 }))
                      }}
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    {outdoor ? 'Outdoor' : 'Indoor'}
                  </label>
                ))}
              </div>
            </div>

            {/* Feature 4: Network details */}
            <div className="fp-props-section" style={{ marginTop: 8 }}>
              <div className="fp-props-label" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>Network</div>
              {([
                ['ip_address', 'IP Address', '10.40.104.101'],
                ['mac_address', 'MAC Address', 'BC:5E:33:C0:1E:2E'],
                ['vlan', 'Subnet/VLAN', 'VLAN 104'],
              ] as const).map(([field, label, placeholder]) => (
                <div key={field} className="fp-props-field" style={{ marginBottom: 4 }}>
                  <label className="fp-props-label" style={{ fontSize: 10 }}>{label}</label>
                  <input
                    className="fp-props-input"
                    value={(selected as unknown as Record<string, unknown>)[field] as string || ''}
                    placeholder={placeholder}
                    onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, [field]: e.target.value } : d))}
                    onBlur={() => { if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { [field]: (selected as unknown as Record<string, unknown>)[field] })) }}
                  />
                </div>
              ))}
            </div>

            {/* Infrastructure */}
            <div className="fp-props-section" style={{ marginTop: 8 }}>
              <div className="fp-props-label" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>Infrastructure</div>
              <div className="fp-props-field" style={{ marginBottom: 4 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>IDF</label>
                <select className="fp-props-input" value={selected.idf_id} onChange={e => {
                  setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, idf_id: e.target.value } : d))
                  if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { idf_id: e.target.value }))
                }}>
                  <option value="">—</option>
                  {infra.filter(i => i.type === 'mdf' || i.type === 'idf').map(i => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>
              <div className="fp-props-field" style={{ marginBottom: 4 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Switch</label>
                <select className="fp-props-input" value={selected.switch_device_id} onChange={e => {
                  setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, switch_device_id: e.target.value } : d))
                  if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { switch_device_id: e.target.value }))
                }}>
                  <option value="">—</option>
                  {devices.filter(d => d.type === 'switch').map(d => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </div>
              {([
                ['switch_port', 'Switch Port', 'Port 7'],
                ['patch_port', 'Patch Port', 'Port 7'],
              ] as const).map(([field, label, placeholder]) => (
                <div key={field} className="fp-props-field" style={{ marginBottom: 4 }}>
                  <label className="fp-props-label" style={{ fontSize: 10 }}>{label}</label>
                  <input
                    className="fp-props-input" value={(selected as unknown as Record<string, unknown>)[field] as string || ''} placeholder={placeholder}
                    onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, [field]: e.target.value } : d))}
                    onBlur={() => { if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { [field]: (selected as unknown as Record<string, unknown>)[field] })) }}
                  />
                </div>
              ))}
            </div>

            {/* Installation */}
            <div className="fp-props-section" style={{ marginTop: 8 }}>
              <div className="fp-props-label" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>Installation</div>
              <div className="fp-props-field" style={{ marginBottom: 4 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Mount Type</label>
                <select className="fp-props-input" value={selected.mount_type} onChange={e => {
                  setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, mount_type: e.target.value } : d))
                  if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { mount_type: e.target.value }))
                }}>
                  <option value="">—</option>
                  {MOUNT_TYPES.map(mt => <option key={mt} value={mt}>{mt}</option>)}
                </select>
              </div>
              <div className="fp-props-field" style={{ marginBottom: 4 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Cable Type</label>
                <select className="fp-props-input" value={selected.cable_type} onChange={e => {
                  setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, cable_type: e.target.value } : d))
                  if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { cable_type: e.target.value }))
                }}>
                  <option value="">—</option>
                  {CABLE_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                </select>
              </div>
              <div className="fp-props-field" style={{ marginBottom: 4 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Cable Length</label>
                <input className="fp-props-input" type="number" value={selected.cable_length_ft ?? ''} placeholder="ft"
                  onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, cable_length_ft: e.target.value ? Number(e.target.value) : null } : d))}
                  onBlur={() => { if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { cable_length_ft: selected.cable_length_ft })) }}
                />
              </div>
              <div className="fp-props-field" style={{ marginBottom: 4 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Status</label>
                <select className="fp-props-input" value={selected.install_status} onChange={e => {
                  setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, install_status: e.target.value } : d))
                  if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { install_status: e.target.value }))
                }}>
                  {INSTALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="fp-props-field" style={{ marginBottom: 4 }}>
                <label className="fp-props-label" style={{ fontSize: 10 }}>Installed By</label>
                <input className="fp-props-input" value={selected.installed_by} placeholder="Name"
                  onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, installed_by: e.target.value } : d))}
                  onBlur={() => { if (selected.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { installed_by: selected.installed_by })) }}
                />
              </div>
            </div>

            {/* Camera-specific controls */}
            {selected.type === 'camera' && (
              <div className="fp-props-section" style={{ marginTop: 8 }}>
                <div className="fp-props-label" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>Camera</div>
                <div className="fp-props-field">
                  <label className="fp-props-label">Angle: {selected.angle}{'\u00B0'}</label>
                  <input
                    type="range" min={-180} max={180} value={selected.angle}
                    onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, angle: Number(e.target.value) } : d))}
                    onMouseUp={() => {
                      if (selected.slug) {
                        showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { rotation: selected.angle }))
                      }
                    }}
                    className="fp-props-slider"
                  />
                </div>
                <div className="fp-props-field">
                  <label className="fp-props-label">FOV: {selected.fov}{'\u00B0'}</label>
                  <input
                    type="range" min={10} max={360} value={selected.fov}
                    onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, fov: Number(e.target.value) } : d))}
                    onMouseUp={() => {
                      if (selected.slug) {
                        showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { h_fov_degrees: selected.fov }))
                      }
                    }}
                    className="fp-props-slider"
                  />
                </div>
                <div className="fp-props-field">
                  <label className="fp-props-label">Range: {selected.range}ft</label>
                  <input
                    type="range" min={20} max={300} value={selected.range}
                    onChange={e => setDevices(prev => prev.map(d => d.id === selected.id ? { ...d, range: Number(e.target.value) } : d))}
                    onMouseUp={() => {
                      if (selected.slug) {
                        showSaving(() => api.patch(`${apiBase}/${slug}/devices/${selected.slug}`, { max_range_ft: selected.range }))
                      }
                    }}
                    className="fp-props-slider"
                  />
                </div>
              </div>
            )}

            <div className="fp-props-section" style={{ marginTop: 8 }}>
              <div className="fp-props-label">Connections</div>
              {cables.filter(c => c.from === selected.id || c.to === selected.id).map(c => {
                const otherId = c.from === selected.id ? c.to : c.from
                const other = devices.find(d => d.id === otherId)
                const fromD = devices.find(d => d.id === c.from)
                const toD = devices.find(d => d.id === c.to)
                const cableDist = fromD && toD ? Math.sqrt(Math.pow(toD.x - fromD.x, 2) + Math.pow(toD.y - fromD.y, 2)) : 0
                const cableFeet = calibrated && cableDist ? (cableDist / scale).toFixed(1) : null
                return (
                  <div key={c.id} className="fp-props-cable">
                    <span style={{ color: c.color }}>{'\u2501\u2501'}</span>
                    <span>{other?.label || '?'}</span>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>{c.type}</span>
                    {cableFeet && <span style={{ color: 'var(--text-tertiary)', fontSize: 10, marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>{cableFeet}ft</span>}
                  </div>
                )
              })}
              {cables.filter(c => c.from === selected.id || c.to === selected.id).length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>No connections</div>
              )}
            </div>

            <div style={{ marginTop: 'auto', paddingTop: 16 }}>
              <button className="fp-props-delete-btn" onClick={deleteSelected}>Delete Device</button>
            </div>
          </div>
        )}

        {/* ── DRAGGABLE LAYERS PANEL ── */}
        {showLayers && (
          <div
            className="fp-legend"
            style={{ left: layersPos.x, top: layersPos.y, minWidth: 200 }}
            onMouseDown={e => e.stopPropagation()}
          >
            <div
              className="fp-legend-titlebar"
              onMouseDown={e => {
                setLayersDragging(true)
                layersDragOffset.current = { x: e.clientX - layersPos.x, y: e.clientY - layersPos.y }
                e.preventDefault()
              }}
            >
              <span style={{ cursor: 'grab', marginRight: 6, color: 'var(--text-tertiary)' }}>{'\u2261'}</span>
              <span className="fp-legend-title">Layers</span>
              <button className="fp-legend-btn" onClick={() => setShowLayers(false)} title="Close">{'\u2715'}</button>
            </div>
            <div className="fp-legend-body" style={{ padding: 8 }}>
              {layers.map(l => (
                <label key={l.id} className="fp-layer-row">
                  <input type="checkbox" checked={l.visible} onChange={() => toggleLayer(l.id)} style={{ accentColor: l.color }} />
                  <span className="fp-layer-dot" style={{ background: l.color }} />
                  <span style={{ color: l.visible ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{l.label}</span>
                  <span className="fp-layer-count">
                    {l.id === 'cables' ? cables.length
                      : l.id === 'measurements' ? '\u2014'
                      : devices.filter(d => (LAYER_MAP[d.type] || 'infrastructure') === l.id).length
                        + infra.filter(inf => (LAYER_MAP[inf.type] || 'infrastructure') === l.id).length}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* ── DRAGGABLE LEGEND PANEL ── */}
        {showLegend && (() => {
          const LEGEND_CATEGORIES = [
            { id: 'cameras', label: 'CAMERAS', types: ['camera'], color: 'var(--color-camera)' },
            { id: 'networking', label: 'NETWORKING', types: ['switch', 'ap'], color: 'var(--color-networking)' },
            { id: 'access', label: 'ACCESS CONTROL', types: ['reader', 'controller'], color: 'var(--color-access)' },
            { id: 'power', label: 'POWER', types: ['power'], color: 'var(--color-power)' },
            { id: 'infrastructure', label: 'INFRASTRUCTURE', types: ['nvr', 'server', 'mdf', 'idf'], color: 'var(--color-infra)' },
          ]
          const statusColor = (s: string) => {
            switch (s.toLowerCase()) { case 'active': return 'var(--ok)'; case 'installed': return 'var(--info)'; case 'offline': return 'var(--err)'; case 'decommissioned': return 'var(--color-infra)'; default: return 'var(--warn)' }
          }
          let globalIdx = 0
          return (
            <div
              className="fp-legend"
              style={{ left: legendPos.x, top: legendPos.y }}
              onMouseDown={e => e.stopPropagation()}
            >
              {/* Title bar */}
              <div
                className="fp-legend-titlebar"
                onMouseDown={e => {
                  setLegendDragging(true)
                  legendDragOffset.current = { x: e.clientX - legendPos.x, y: e.clientY - legendPos.y }
                  e.preventDefault()
                }}
              >
                <span style={{ cursor: 'grab', marginRight: 6, color: 'var(--text-tertiary)' }}>{'\u2261'}</span>
                <span className="fp-legend-title">Legend</span>
                <button className="fp-legend-btn" onClick={() => setLegendCollapsed(c => !c)} title={legendCollapsed ? 'Expand' : 'Collapse'}>{legendCollapsed ? '+' : '\u2212'}</button>
                <button className="fp-legend-btn" onClick={() => setLegendCompact(c => !c)} title={legendCompact ? 'Expanded view' : 'Compact view'}>{'\u25A1'}</button>
                <button className="fp-legend-btn" onClick={() => setShowLegend(false)} title="Close">{'\u2715'}</button>
              </div>

              {/* Body */}
              {!legendCollapsed && (
                <>
                  <div className="fp-legend-body">
                    {legendCompact ? (
                      /* Compact mode */
                      devices.map((d, i) => (
                        <div key={d.id} className="fp-legend-row" onClick={() => legendSelectDevice(d)}
                          onDoubleClick={() => { legendSelectDevice(d); setZoom(2) }}>
                          <span style={{ width: 8, height: 8, borderRadius: 4, background: d.color, flexShrink: 0 }} />
                          <span className="fp-legend-num">{i + 1}</span>
                          <span className="fp-legend-label" style={{ color: d.color }}>{d.label}</span>
                          <span className="fp-legend-name">{d.name}</span>
                        </div>
                      ))
                    ) : (
                      /* Expanded mode — grouped by category */
                      LEGEND_CATEGORIES.map(cat => {
                        const catDevices = devices.filter(d => cat.types.includes(d.type))
                        if (catDevices.length === 0) return null
                        const isCollapsed = collapsedCategories.has(cat.id)
                        return (
                          <div key={cat.id}>
                            <div
                              className="fp-legend-category-header"
                              style={{ color: cat.color }}
                              onClick={() => setCollapsedCategories(prev => {
                                const next = new Set(prev)
                                if (next.has(cat.id)) next.delete(cat.id); else next.add(cat.id)
                                return next
                              })}
                            >
                              <span style={{ fontSize: 8 }}>{isCollapsed ? '\u25B6' : '\u25BC'}</span>
                              {cat.label} ({catDevices.length})
                            </div>
                            {!isCollapsed && catDevices.map(d => {
                              globalIdx++
                              return (
                                <div key={d.id} className="fp-legend-row" onClick={() => legendSelectDevice(d)}
                                  onDoubleClick={() => { legendSelectDevice(d); setZoom(2) }}>
                                  <span className="fp-legend-num">{globalIdx}</span>
                                  <svg width={16} height={16} viewBox="0 0 28 28"><DeviceIcon type={d.icon} size={28} color={d.color} /></svg>
                                  <span className="fp-legend-label" style={{ color: d.color }}>{d.label}</span>
                                  <span className="fp-legend-name">{d.name}</span>
                                  <span className="fp-legend-status" style={{ background: statusColor(d.install_status) }} title={d.install_status} />
                                </div>
                              )
                            })}
                          </div>
                        )
                      })
                    )}
                  </div>
                  {/* Footer */}
                  <div className="fp-legend-footer">
                    {devices.length} devices {'\u00B7'} {cables.length} cable runs {'\u00B7'}{' '}
                    {calibrated ? (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999, background: 'var(--ok-m)', color: 'var(--ok)' }}>
                        1px = {(1 / scale).toFixed(2)}ft
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999, background: 'var(--warn-m)', color: 'var(--warn)' }}>
                        Not calibrated
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        })()}

        {/* ── CALIBRATION MODAL ── */}
        {showCalibModal && calibPoints.length === 2 && (
          <div className="fp-modal-overlay" onClick={() => { setShowCalibModal(false); setCalibPoints([]) }}>
            <div className="fp-modal" onClick={e => e.stopPropagation()}>
              <div className="fp-modal-title">Calibrate Scale</div>
              <p className="fp-modal-text">
                You selected two points <b>{fmt(Math.sqrt(Math.pow(calibPoints[1].x - calibPoints[0].x, 2) + Math.pow(calibPoints[1].y - calibPoints[0].y, 2)))}px</b> apart.
              </p>
              <label className="fp-modal-label">What is the real distance between them?</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="fp-modal-input"
                  type="number" min={0} step="any"
                  value={calibInput}
                  onChange={e => setCalibInput(e.target.value)}
                  placeholder="e.g. 25"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') applyCalibration() }}
                />
                <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>feet</span>
              </div>
              {calibInput && parseFloat(calibInput) > 0 && (
                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>
                  Scale will be set to: 1 pixel = {(1 / (Math.sqrt(Math.pow(calibPoints[1].x - calibPoints[0].x, 2) + Math.pow(calibPoints[1].y - calibPoints[0].y, 2)) / parseFloat(calibInput))).toFixed(3)}ft
                </p>
              )}
              <div className="fp-modal-actions">
                <button className="fp-modal-btn secondary" onClick={() => { setShowCalibModal(false); setCalibPoints([]) }}>Cancel</button>
                <button className="fp-modal-btn primary" onClick={applyCalibration}>Apply Scale</button>
              </div>
            </div>
          </div>
        )}

        {/* ── ROOM NAME MODAL ── */}
        {showRoomModal && (
          <div className="fp-modal-overlay" onClick={() => { setShowRoomModal(false); setRoomVertices([]) }}>
            <div className="fp-modal" onClick={e => e.stopPropagation()}>
              <div className="fp-modal-title">Name this room</div>
              <input
                className="fp-modal-input"
                value={roomNameInput}
                onChange={e => setRoomNameInput(e.target.value)}
                placeholder="e.g. Lobby"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') saveRoom() }}
              />
              <div className="fp-modal-actions">
                <button className="fp-modal-btn secondary" onClick={() => { setShowRoomModal(false); setRoomVertices([]) }}>Cancel</button>
                <button className="fp-modal-btn primary" onClick={saveRoom}>Save</button>
              </div>
            </div>
          </div>
        )}

        {/* ── MDF/IDF MODAL ── */}
        {showMdfModal && (
          <div className="fp-modal-overlay" onClick={() => { setShowMdfModal(false); setMdfPendingPos(null) }}>
            <div className="fp-modal" onClick={e => e.stopPropagation()}>
              <div className="fp-modal-title">Infrastructure Point</div>
              <div style={{ display: 'flex', gap: 16, margin: '12px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="radio" name="mdf-type" checked={mdfType === 'mdf'} onChange={() => setMdfType('mdf')} style={{ accentColor: 'var(--accent)' }} />
                  MDF
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="radio" name="mdf-type" checked={mdfType === 'idf'} onChange={() => setMdfType('idf')} style={{ accentColor: 'var(--accent)' }} />
                  IDF
                </label>
              </div>
              <label className="fp-modal-label">Name</label>
              <input
                className="fp-modal-input"
                value={mdfNameInput}
                onChange={e => setMdfNameInput(e.target.value)}
                placeholder="e.g. IDF-01"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') placeMdf() }}
              />
              <div className="fp-modal-actions">
                <button className="fp-modal-btn secondary" onClick={() => { setShowMdfModal(false); setMdfPendingPos(null) }}>Cancel</button>
                <button className="fp-modal-btn primary" onClick={placeMdf}>Place</button>
              </div>
            </div>
          </div>
        )}

        {/* ── 3D SCAN MODAL ── */}
        {showScan3dModal && (
          <div className="fp-modal-overlay" onClick={() => setShowScan3dModal(false)}>
            <div className="fp-modal" onClick={e => e.stopPropagation()}>
              <div className="fp-modal-title">3D Scan Viewer</div>
              <label className="fp-modal-label">Paste the URL to your 3D scan (e.g. Matterport, ScanBuilds)</label>
              <input
                className="fp-modal-input"
                value={scan3dUrl}
                onChange={e => setScan3dUrl(e.target.value)}
                placeholder="https://portal.scanbuilds.com/sharedSpace/..."
                autoFocus
              />
              <div className="fp-modal-actions">
                <button className="fp-modal-btn secondary" onClick={() => setShowScan3dModal(false)}>Cancel</button>
                {scan3dUrl && (
                  <button className="fp-modal-btn secondary" onClick={() => window.open(scan3dUrl, '_blank')}>Open 3D Viewer</button>
                )}
                <button className="fp-modal-btn primary" onClick={() => setShowScan3dModal(false)}>Save</button>
              </div>
            </div>
          </div>
        )}

        {/* ── LABEL CONFIG MODAL ── */}
        {showLabelConfig && (
          <div className="fp-modal-overlay" onClick={() => setShowLabelConfig(false)}>
            <div className="fp-modal" onClick={e => e.stopPropagation()} style={{ minWidth: 400 }}>
              <div className="fp-modal-title">Device Labeling</div>
              {(['camera', 'switch', 'ap', 'reader', 'controller', 'power', 'nvr'] as const).map(type => {
                const cfg = (labelConfig as unknown as Record<string, LabelTypeConfig | number>)[type] as LabelTypeConfig
                return (
                  <div key={type} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 50px 50px', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{type}</span>
                    <input
                      className="fp-modal-input" placeholder="Prefix" value={cfg.prefix}
                      style={{ fontSize: 11, padding: '4px 6px' }}
                      onChange={e => setLabelConfig(prev => ({ ...prev, [type]: { ...cfg, prefix: e.target.value } }))}
                    />
                    <input
                      className="fp-modal-input" type="number" min={0} max={4} value={cfg.padding}
                      style={{ fontSize: 11, padding: '4px 6px' }}
                      onChange={e => setLabelConfig(prev => ({ ...prev, [type]: { ...cfg, padding: Number(e.target.value) } }))}
                    />
                    <input
                      className="fp-modal-input" placeholder="Sfx" value={cfg.suffix}
                      style={{ fontSize: 11, padding: '4px 6px' }}
                      onChange={e => setLabelConfig(prev => ({ ...prev, [type]: { ...cfg, suffix: e.target.value } }))}
                    />
                  </div>
                )
              })}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Floor offset:</label>
                <input
                  className="fp-modal-input" type="number" min={0} value={labelConfig.floorOffset}
                  style={{ width: 60, fontSize: 11, padding: '4px 6px' }}
                  onChange={e => setLabelConfig(prev => ({ ...prev, floorOffset: Number(e.target.value) }))}
                />
                <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>(Floor 2 cameras start at {labelConfig.camera.prefix}{(2 * (labelConfig.floorOffset || 100)) + 1})</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-tertiary)' }}>
                Preview: {labelConfig.camera.prefix}{labelConfig.camera.padding > 0 ? '1'.padStart(labelConfig.camera.padding, '0') : '1'}{labelConfig.camera.suffix}, {labelConfig.camera.prefix}{labelConfig.camera.padding > 0 ? '2'.padStart(labelConfig.camera.padding, '0') : '2'}{labelConfig.camera.suffix}, {labelConfig.camera.prefix}{labelConfig.camera.padding > 0 ? '3'.padStart(labelConfig.camera.padding, '0') : '3'}{labelConfig.camera.suffix}...
              </div>
              <div className="fp-modal-actions">
                <button className="fp-modal-btn secondary" onClick={() => setShowLabelConfig(false)}>Cancel</button>
                <button className="fp-modal-btn primary" onClick={() => setShowLabelConfig(false)}>Save</button>
              </div>
            </div>
          </div>
        )}

        {/* ── CONTEXT MENU ── */}
        {contextMenu && (
          <div
            className="fp-context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            {contextMenu.deviceId ? (
              <>
                <button className="fp-ctx-item" onClick={() => {
                  setSelectedId(contextMenu.deviceId!); setShowProperties(true); closeContextMenu()
                }}>Properties</button>
                <button className="fp-ctx-item" onClick={() => {
                  // Duplicate device
                  const dev = devices.find(d => d.id === contextMenu.deviceId)
                  if (dev) {
                    const newDev: CanvasDevice = { ...dev, id: `d${Date.now()}`, slug: undefined, x: dev.x + 30, y: dev.y + 30, label: dev.label + '-copy', install_status: 'Planned' }
                    setDevices(prev => [...prev, newDev])
                    pushUndo({ type: 'add', device: newDev })
                    if (plan) {
                      showSaving(() =>
                        api.post<PlacedDeviceType>(`${apiBase}/${slug}/devices`, {
                          x: Math.round(newDev.x), y: Math.round(newDev.y), rotation: newDev.angle,
                          label: newDev.label, device_type: newDev.type,
                          h_fov_degrees: newDev.fov || null, max_range_ft: newDev.range || null,
                        }).then(eq => {
                          setDevices(p => p.map(d => d.id === newDev.id ? { ...d, id: eq.id, slug: eq.slug } : d))
                        })
                      )
                    }
                  }
                  closeContextMenu()
                }}>Duplicate</button>
                <button className="fp-ctx-item" onClick={() => {
                  setTool(TOOLS.CABLE); setCableStart(contextMenu.deviceId!); closeContextMenu()
                }}>Draw Cable From Here</button>
                <div className="fp-ctx-divider" />
                <button className="fp-ctx-item" onClick={() => {
                  const dev = devices.find(d => d.id === contextMenu.deviceId)
                  if (dev) {
                    const newLabel = prompt('Change label:', dev.label)
                    if (newLabel !== null) {
                      setDevices(prev => prev.map(d => d.id === dev.id ? { ...d, label: newLabel } : d))
                      if (dev.slug) showSaving(() => api.patch(`${apiBase}/${slug}/devices/${dev.slug}`, { label: newLabel }))
                    }
                  }
                  closeContextMenu()
                }}>Change Label</button>
                <div className="fp-ctx-divider" />
                <button className="fp-ctx-item danger" onClick={() => {
                  setSelectedId(contextMenu.deviceId!); closeContextMenu()
                  setTimeout(deleteSelected, 0)
                }}>Delete</button>
              </>
            ) : (
              <>
                <button className="fp-ctx-item" onClick={() => { setTool(TOOLS.PLACE); setShowCatalog(true); closeContextMenu() }}>Place Device</button>
                <button className="fp-ctx-item" onClick={() => { setTool(TOOLS.MDF); closeContextMenu() }}>Add MDF/IDF</button>
                <button className="fp-ctx-item" onClick={() => { setTool(TOOLS.ROOM); setRoomVertices([]); closeContextMenu() }}>Create Room</button>
                <div className="fp-ctx-divider" />
                <button className="fp-ctx-item" onClick={() => { setTool(TOOLS.MEASURE); setMeasurePoints([]); closeContextMenu() }}>Measure Distance</button>
                <button className="fp-ctx-item" onClick={() => { setTool(TOOLS.CALIBRATE); setCalibPoints([]); closeContextMenu() }}>Calibrate Scale</button>
                <div className="fp-ctx-divider" />
                <button className="fp-ctx-item" onClick={() => {
                  // Fit to view
                  if (devices.length === 0) { setZoom(1); setPan({ x: 0, y: 0 }); closeContextMenu(); return }
                  const svg = svgRef.current
                  if (!svg) { closeContextMenu(); return }
                  const rect = svg.getBoundingClientRect()
                  const xs = devices.map(d => d.x); const ys = devices.map(d => d.y)
                  const minX = Math.min(...xs) - 50; const maxX = Math.max(...xs) + 50
                  const minY = Math.min(...ys) - 50; const maxY = Math.max(...ys) + 50
                  const fitZoom = Math.min(rect.width / (maxX - minX), rect.height / (maxY - minY), 3)
                  setZoom(fitZoom)
                  setPan({ x: rect.width / 2 - ((minX + maxX) / 2) * fitZoom, y: rect.height / 2 - ((minY + maxY) / 2) * fitZoom })
                  closeContextMenu()
                }}>Fit to View</button>
                <button className="fp-ctx-item" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); closeContextMenu() }}>Reset Zoom</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── EQUIPMENT COUNT BAR ── */}
      <div className="fp-count-bar">
        {Object.entries(
          devices.reduce<Record<string, number>>((acc, d) => { acc[d.type] = (acc[d.type] || 0) + 1; return acc }, {})
        ).map(([type, count]) => (
          <span key={type} className="fp-count-item">
            <span style={{ color: colorForType(type) }}>{'\u25CF'}</span>
            {count} {type}{count > 1 ? 's' : ''}
          </span>
        ))}
        {devices.length === 0 && <span style={{ color: 'var(--text-tertiary)' }}>0 devices</span>}
        <span style={{ marginLeft: 'auto', color: 'var(--text-tertiary)' }}>{cables.length} cable runs</span>
      </div>
    </div>
  )
}

// Error boundary wrapper to catch render errors without triggering route error boundary
class FloorPlanErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[FloorPlanDesigner] Render error:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      const role = useAuthStore.getState().user?.role
      const isAdmin = role === 'super_admin' || role === 'admin'

      return (
        <div className="fp-root" style={{ justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ fontSize: 48, opacity: 0.3, marginBottom: 16 }}>!</div>
          <div style={{ color: 'var(--danger)', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            Something went wrong
          </div>
          <div style={{ color: 'var(--fp-text-muted)', fontSize: 13, marginBottom: 16 }}>
            {this.state.error?.message || 'An unexpected error occurred in the floor plan designer'}
          </div>
          {isAdmin && this.state.error?.stack && (
            <pre
              style={{
                fontSize: 10, padding: 12, borderRadius: 8,
                background: 'rgba(0,0,0,0.3)', color: 'var(--fp-text-muted)',
                maxWidth: 600, maxHeight: 200, overflow: 'auto',
                textAlign: 'left', whiteSpace: 'pre-wrap', marginBottom: 16,
                cursor: 'copy',
              }}
              onClick={() => navigator.clipboard?.writeText(this.state.error?.stack || '')}
              title="Click to copy"
            >
              {this.state.error.stack}
            </pre>
          )}
          <button
            className="btn-primary"
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginRight: 8 }}
          >
            Try Again
          </button>
          <button
            className="btn-secondary"
            onClick={() => window.history.back()}
          >
            Go Back
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function FloorPlanDesigner() {
  return (
    <FloorPlanErrorBoundary>
      <FloorPlanDesignerInner />
    </FloorPlanErrorBoundary>
  )
}
